'use client'

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import Link from 'next/link'
import AdvisorChat from '@/components/platform/AdvisorChat'
import ReadinessStrategistEntry from '@/components/platform/ReadinessStrategistEntry'
import { PackModal } from '@/components/platform/PackModal'
import { SignInLink } from '@/components/platform/SignInLink'
import RadarTour from '@/components/platform/RadarTour'
import { RadarLockup } from '@/components/brand/RadarMark'
import {
  Button,
  IconButton,
  Input,
  useSuccessFlash,
} from '@/components/platform/ui'
import { openCheckout } from '@/lib/paddle/checkout'
import { radarAnalytics } from '@/lib/analytics/radar-events'
import SmartCTA from '@/components/platform/SmartCTA'
import {
  DetailsTab,
  ActionsPanel,
  DashboardOverview,
  DashboardSkeleton,
  LLMAnswerDiff,
  DashboardShell,
  DashboardTopBar,
  NewAuditView,
  Panel,
  Chip,
  GradeChip,
  buildNavModel,
  InstrumentLabel,
} from '@/components/radar'
import type { RadarTab } from '@/components/radar'
import { peekVisitorId } from '@/lib/analytics/visitor-id'
import {
  Loader2,
  Bot,
  FileText,
  Quote,
  Shield,
  Zap,
  MessageCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Play,
  CircleDot,
  Sparkles,
  ExternalLink,
  Download,
  GitCompare,
  Swords,
  Trash2,
  Plus,
  Printer,
  ScanSearch,
  SearchCheck,
  Waypoints,
  BarChart3,
  ShieldCheck,
  BrainCircuit,
  Fingerprint,
  RefreshCw,
  Mail,
  ArrowUp,
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  Minus,
  Pencil,
  X,
  Check,
  AlertTriangle,
} from 'lucide-react'

// ============================================================================
// TYPES & SHARED MODULES
// ============================================================================

import type { ToolState, ThreadLog, SavedRun, ToolKey } from '@/types/radar'
import { TOOLS as TOOLS_DATA } from '@/types/radar'
import {
  GRADE_COLORS,
  EFFORT_LABELS,
  SCORE_TEXT_GRADIENT,
  gradeFromScore,
} from '@/lib/radar/scoring'
import {
  aggregateToolScores,
  hasUsableScore,
  usableScore,
} from '@/lib/radar/tool-scores'
import {
  canRunWithoutConfirmation,
  entityDomainKey,
  isAssertableEntity,
  type AuditEntity,
} from '@/lib/radar/entity-types'
import { buildCitationTrendFields } from '@/lib/radar/trend-payload'
import {
  planToolBatches,
  runSequentialToolBatches,
} from '@/lib/radar/tool-batches'
import { generateInsights } from '@/lib/radar/insights'
import { generateActions } from '@/lib/radar/actions'
import {
  sortActions,
  RADAR_ACTION_METHOD_VERSION,
} from '@/lib/radar/action-priority'
import { mergeCanonicalActions } from '@/lib/radar/gsc-merge'
import { useGscEnrichment } from '@/lib/radar/use-gsc-enrichment'
import { buildThreads } from '@/lib/radar/threads'
import { ThreadsPanel } from '@/components/radar/ThreadsPanel'
import { MultiDomainDashboard } from '@/components/radar/MultiDomainDashboard'
import {
  loadProgress,
  saveProgress,
  toggleStepComplete,
  type ProgressMap,
} from '@/lib/radar/thread-progress'
import { addWin, loadWinLog } from '@/lib/radar/win-log'
import type { WinLogEntry } from '@/types/radar'
import { generateSuggestedQuestions } from '@/lib/advisor/audit-context-builder'
import { AnimatedThemeToggle } from '@/components/ui/AnimatedThemeToggle'
import { platformHeaders } from '@/lib/platform/client-headers'
import { failingCheckCount, findPreviousRun } from '@/lib/radar/run-history'

/**
 * "1d ago" style label for the top-bar status line. Kept local because it is
 * a one-line display concern; ScoreInterpretation has its own richer
 * formatter for the comparison narrative.
 */
function relativeScanLabel(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

// Icons must stay in page.tsx because they are React components (can't serialize)
const TOOL_ICONS: Record<string, typeof Bot> = {
  crawl: Bot,
  robots: Shield,
  llms: FileText,
  readiness: Zap,
  citation: Quote,
  reddit: MessageCircle,
  aeo: ScanSearch,
  answer: SearchCheck,
  sourceInfluence: Waypoints,
  promptSov: BarChart3,
  schemaAudit: ShieldCheck,
  hallucinationCheck: BrainCircuit,
  brandDisambiguation: Fingerprint,
}

// Merge shared TOOLS data with icons for rendering. All code below references this.
const TOOLS = TOOLS_DATA.map(t => ({
  ...t,
  icon: TOOL_ICONS[t.key] || Bot,
}))

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function PlatformDashboard() {
  // Default to dark theme on platform if user hasn't explicitly chosen.
  // Since 2026-07-10 dark is the site-wide default (ThemeProvider), so
  // this is a belt-and-suspenders guard for the platform surface.
  // AnimatedThemeToggle persists any explicit user choice in
  // localStorage, so this only affects first-visit state.
  useEffect(() => {
    const stored = localStorage.getItem('theme')
    if (!stored) {
      document.documentElement.setAttribute('data-theme', 'dark')
      localStorage.setItem('theme', 'dark')
    }
  }, [])

  // Local-dev bypass: when running `npm run dev`, skip the access gate and
  // scan-used limit so we can iterate without email/token capture. Production
  // is untouched because Next.js inlines NODE_ENV at build time.
  const isDev = process.env.NODE_ENV === 'development'

  // Access gate: free-tier visitors must request a token by email (lead
  // capture). Paid users with a session cookie bypass the gate entirely.
  const [accessGranted, setAccessGranted] = useState(false)
  const [accessLoading, setAccessLoading] = useState(true)
  const [, setAccessUser] = useState<{
    email: string
    name: string
    expiresAt: string
  } | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)

  // User tier drives "locked" state on tier-2 tools inside the app.
  const [userTier, setUserTier] = useState<'free' | 'paid'>('free')
  // Drives the inline Readiness Strategist entry card -> opens AdvisorChat seeded.
  const [strategistTrigger, setStrategistTrigger] = useState<{
    question: string
    nonce: number
  } | null>(null)

  useEffect(() => {
    async function resolveAccess() {
      if (isDev) {
        setAccessGranted(true)
        setUserTier('paid')
        setAccessLoading(false)
        return
      }
      // 1. Session-based paid users bypass the gate. The tier endpoint
      //    returns { email } for any authenticated identity (session or
      //    valid token) plus the resolved tier from payment state.
      try {
        // resolveUserTier falls back to the x-platform-token header when there
        // is no session cookie (lib/radar/tier-check.ts). Sent bare, a
        // token-only paid user resolves as free and loses access.
        const tierRes = await fetch('/api/platform/tier', {
          headers: platformHeaders(),
        })
        if (tierRes.ok) {
          const tierData = await tierRes.json()
          setUserTier(tierData?.tier === 'paid' ? 'paid' : 'free')
          if (tierData?.email) {
            setAccessGranted(true)
            setAccessLoading(false)
            return
          }
        }
      } catch {
        // Fall through to token check
      }

      // 2. URL token or localStorage token: validate and grant.
      const params = new URLSearchParams(window.location.search)
      const urlToken = params.get('token')
      const storedToken = localStorage.getItem('pm_platform_token')
      const token = urlToken || storedToken

      if (!token) {
        setAccessLoading(false)
        return
      }

      try {
        const res = await fetch('/api/platform/validate-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        const data = await res.json()
        if (res.ok && data.valid) {
          localStorage.setItem('pm_platform_token', token)
          setAccessUser({
            email: data.email,
            name: data.name,
            expiresAt: data.expiresAt,
          })
          setAccessGranted(true)
          if (urlToken) {
            window.history.replaceState({}, '', '/platform/app')
          }
        } else {
          localStorage.removeItem('pm_platform_token')
          setTokenError(data.message || 'Invalid token.')
        }
      } catch {
        setTokenError('Could not validate token.')
      }
      setAccessLoading(false)
    }
    resolveAccess()
  }, [isDev])

  // Product analytics (PostHog-only, via the radar-events adapter). The
  // pending/viewed refs make each lifecycle event fire exactly once.
  const gateViewedRef = useRef(false)
  useEffect(() => {
    if (accessLoading || accessGranted || gateViewedRef.current) return
    gateViewedRef.current = true
    radarAnalytics.gateViewed()
  }, [accessLoading, accessGranted])
  // Set in handleRun; consumed by the audit-completed effect after the
  // final render so the event carries the real unified score and counts.
  const auditStartedAtRef = useRef<number | null>(null)
  const auditCompletePendingRef = useRef(false)

  // Post-$5 auto-run: a platform_unlock buyer signs in via the magic link and
  // lands here as /platform/app?domain=<their domain>. Once access resolves to
  // paid (the $5 credit), run the full 13-tool audit for that domain once, then
  // clean the URL so a refresh doesn't re-trigger it.
  const autoRunRef = useRef(false)
  useEffect(() => {
    if (autoRunRef.current) return
    if (!accessGranted) return
    if (userTier !== 'paid' && !isDev) return
    const autoDomain = new URLSearchParams(window.location.search).get('domain')
    if (!autoDomain) return
    autoRunRef.current = true
    window.history.replaceState({}, '', '/platform/app')
    setDomain(autoDomain)
    void handleRun({ preventDefault: () => {} } as React.FormEvent, autoDomain)
    // handleRun is a stable hoisted declaration; deps intentionally minimal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessGranted, userTier])

  /**
   * Deep link to a saved report: /platform/app?run=<id>.
   *
   * The account page lists a customer's audits; before this there was no way
   * to open one from there, so the list looked interactive and did nothing
   * (reported 2026-07-26). This is the destination those rows link to.
   *
   * Deliberately NOT `?domain=` — that param auto-STARTS an audit and spends
   * a credit. A link to an existing report must never charge anyone.
   *
   * The run is fetched owner-scoped by the API, so a guessed id belonging to
   * someone else 404s rather than leaking a report.
   */
  const openRunRef = useRef(false)
  // Same pattern as saveRunRef below: loadRun is declared far past this effect,
  // so naming it as a dependency would evaluate the deps array before the
  // binding exists. Holding it in a ref lets the effect declare its real
  // dependencies instead of suppressing exhaustive-deps.
  const loadRunRef = useRef<((run: SavedRun) => Promise<void>) | null>(null)
  const [openingRun, setOpeningRun] = useState(false)
  const [openRunError, setOpenRunError] = useState<string | null>(null)
  useEffect(() => {
    if (openRunRef.current) return
    if (!accessGranted) return
    const runId = new URLSearchParams(window.location.search).get('run')
    if (!runId) return
    openRunRef.current = true
    window.history.replaceState({}, '', '/platform/app')
    setOpeningRun(true)
    ;(async () => {
      try {
        const res = await fetch(
          `/api/platform/audit-runs?id=${encodeURIComponent(runId)}`,
          { headers: platformHeaders() }
        )
        if (!res.ok) {
          setOpenRunError(
            res.status === 404
              ? 'That report is not available on this account. Try signing in with the email you used to run it.'
              : 'That report could not be opened. Please try again.'
          )
          return
        }
        const { run } = await res.json()
        if (!run?.domain) {
          setOpenRunError('That report could not be opened. Please try again.')
          return
        }
        await loadRunRef.current?.({
          id: run.id,
          domain: run.domain,
          timestamp: run.timestamp ?? Date.now(),
          unifiedScore: run.unifiedScore ?? 0,
          unifiedGrade: run.unifiedGrade ?? 'F',
          toolScores: run.toolScores ?? {},
          toolData: run.toolData ?? {},
        })
        setBrand(run.brand || '')
        setCategory(run.category || '')
        // Restore the identity this run was measured against. Without it the
        // entity from whatever was on screen before survives into the loaded
        // run, and a confirmation made for another domain would carry over.
        setEntity(run.entity ?? null)
        setBrandDetected(!!run.entity)
        setEntityConfirmPending(false)
      } catch {
        setOpenRunError('That report could not be opened. Please try again.')
      } finally {
        setOpeningRun(false)
      }
    })()
  }, [accessGranted])

  // Keyboard shortcuts. Unmodified single-key shortcuts (1-6, s) bail
  // whenever the user is typing in any field — including SELECT and any
  // contenteditable surface, not just INPUT/TEXTAREA. Modified shortcuts
  // (Cmd/Ctrl+Enter, Cmd/Ctrl+P) always fire, since the modifier itself
  // is the disambiguator (eval P5 #25 keyboard-nav).
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement
      const isFormField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable

      // Ctrl/Cmd + Enter: run audit (works even in input)
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        const form = document.querySelector(
          '[data-audit-form]'
        ) as HTMLFormElement
        form?.requestSubmit()
        return
      }

      // Ctrl/Cmd + P: export PDF
      if ((e.metaKey || e.ctrlKey) && e.key === 'p') {
        e.preventDefault()
        exportPDFRef.current?.()
        return
      }

      // Below here is unmodified single-key shortcuts only. Bail if user
      // is typing anywhere OR if a modifier is held (avoids stomping on
      // browser shortcuts like Cmd+1 for tab switching).
      if (isFormField) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      // Number keys 1-6 to switch tabs
      const tabMap: Record<string, typeof activeTab> = {
        '1': 'overview',
        '2': 'actions',
        '3': 'details',
        '4': 'history',
        '5': 'competitor',
        '6': 'threads',
      }
      if (tabMap[e.key]) {
        setActiveTab(tabMap[e.key])
        return
      }

      // S to save run
      if (e.key === 's' || e.key === 'S') {
        saveRunRef.current?.()
        return
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Refs for keyboard shortcut callbacks (avoid stale closures)
  const exportPDFRef = useRef<(() => void) | null>(null)
  const saveRunRef = useRef<(() => void) | null>(null)

  // Mobile navigation drawer. The desktop rail is always present; this only
  // controls the below-lg drawer. (Replaces the old collapsible-sidebar
  // toggle, which had no mobile equivalent at all.)
  const [navOpen, setNavOpen] = useState(false)

  /**
   * New-audit view.
   *
   * Deliberately NOT a route and NOT a separate flow — it is a mode of this
   * same page, so the audit state, handlers and orchestration are shared.
   *
   * Opening it does NOT clear the current audit: `handleNewTest` wipes tools,
   * logs and the saved session, and running that on entry would destroy a
   * report the customer paid for the moment they clicked "Run new audit".
   * The reset is offered explicitly inside the view instead, and `handleRun`
   * resets the tool state on submit anyway.
   */
  const [newAuditRequested, setNewAuditRequested] = useState(false)

  // Inputs
  const [domain, setDomain] = useState('')
  const [brand, setBrand] = useState('')
  const [category, setCategory] = useState('')
  const [brandKeywords, setBrandKeywords] = useState('')
  const [brandDescription, setBrandDescription] = useState('')
  const [locale, setLocale] = useState('global')
  const [inputError, setInputError] = useState<string | null>(null)
  const [activeDomain, setActiveDomain] = useState<string | null>(null)
  // Results-instance ownership for async enrichment. Unlike currentRunId,
  // this changes only when a different audit becomes active, never on save.
  const [auditGeneration, setAuditGeneration] = useState(0)
  // Charge receipt issued by /api/platform/audit/charge. Required by
  // /api/platform/audit-runs POST for authenticated saves so we can't save
  // without a matching credit deduction. Cleared after successful save.
  const [activeChargeId, setActiveChargeId] = useState<string | null>(null)
  const [detectingBrand, setDetectingBrand] = useState(false)
  const [brandDetected, setBrandDetected] = useState(false)
  const lastDetectedDomain = useRef('')

  // Provenance of the resolved entity. Null until a domain has been resolved.
  // `confirmedByUser` is the authoritative bit: once set, no later automatic
  // resolution may overwrite the name, across reruns, refreshes, report
  // regeneration, webhooks or history.
  const [entity, setEntity] = useState<AuditEntity | null>(null)
  // True while the confirmation gate is blocking a run. Anything short of
  // high confidence must be confirmed before we spend on a full audit.
  const [entityConfirmPending, setEntityConfirmPending] = useState(false)

  // Pre-scan form progressive disclosure (eval P2 #13). Advanced options
  // (Brand / Category / Locale) default open when brand has NOT been
  // detected yet so first-time users see they can customize. Tool selector
  // defaults closed since 13 of 13 are pre-selected and most users keep
  // the default set.
  const [advancedOpen, setAdvancedOpen] = useState(!brandDetected)
  const [customizeOpen, setCustomizeOpen] = useState(false)

  // Auto-detect brand when domain input loses focus or after typing stops
  const detectBrand = useCallback(async (urlStr: string) => {
    if (!urlStr.trim()) return
    let normalized = urlStr.trim()
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://'))
      normalized = `https://${normalized}`
    let hostname: string
    try {
      hostname = new URL(normalized).hostname.replace('www.', '')
    } catch {
      return
    }
    if (hostname === lastDetectedDomain.current) return
    lastDetectedDomain.current = hostname

    setDetectingBrand(true)
    setBrandDetected(false)
    setEntityConfirmPending(false)
    try {
      const res = await fetch('/api/tools/detect-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: normalized }),
      })
      if (res.ok) {
        const data = await res.json()
        setBrand(data.brand || '')
        setCategory(data.category || '')
        setBrandKeywords(data.keywords || '')
        setBrandDescription(data.description || '')
        setBrandDetected(true)
        // A fresh domain means a fresh entity: any previous confirmation
        // belonged to a different site and must not carry over.
        setEntity({
          domain: hostname,
          brand: data.brand || '',
          legalName: data.legalName ?? null,
          source: data.source ?? 'domain_fallback',
          confidence: data.confidence ?? 'low',
          requiresConfirmation: data.requiresConfirmation !== false,
          evidence: data.evidence ?? [],
          conflicts: data.conflicts ?? [],
          resolverVersion: data.resolverVersion ?? 'unknown',
          resolvedAt: data.resolvedAt ?? new Date().toISOString(),
          detectedBrand: data.brand || '',
          confirmedByUser: false,
        })
      }
    } catch {
      /* ignore */
    }
    setDetectingBrand(false)
  }, [])

  /**
   * A manual edit to the brand field IS a confirmation. The user has looked at
   * what we resolved and asserted something different, which outranks any
   * automatic resolution from here on — including on paid reruns.
   */
  const handleBrandChange = useCallback(
    (value: string, currentDomain: string) => {
      setBrand(value)
      setEntity(prev =>
        prev
          ? {
              ...prev,
              brand: value,
              source: 'user_confirmed',
              confirmedByUser: true,
              requiresConfirmation: false,
            }
          : {
              domain: entityDomainKey(currentDomain),
              brand: value,
              legalName: null,
              source: 'user_confirmed',
              confidence: 'low',
              requiresConfirmation: false,
              evidence: [],
              conflicts: [],
              resolverVersion: 'unknown',
              resolvedAt: new Date().toISOString(),
              // No resolver ever ran; the detected value is genuinely absent
              // rather than something the user overrode.
              detectedBrand: '',
              confirmedByUser: true,
            }
      )
      setEntityConfirmPending(false)
    },
    []
  )

  /** Explicit "yes, that's us" on the confirmation gate. */
  const confirmEntity = useCallback(() => {
    setEntity(prev =>
      prev
        ? { ...prev, confirmedByUser: true, requiresConfirmation: false }
        : prev
    )
    setEntityConfirmPending(false)
  }, [])

  // Tool selection
  const [selected, setSelected] = useState<Set<ToolKey>>(
    new Set(TOOLS.map(t => t.key))
  )
  const [isRunning, setIsRunning] = useState(false)

  // One scan per token - check if already used
  const [scanUsed, setScanUsed] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  // Session mismatch: paid user has credits/sub but the consume call missed
  // them (case drift, RPC error, etc). The charge route flags this as 409
  // session_mismatch instead of 402 needs_credits so we can show a recovery
  // modal here instead of the pay modal. See migration 080 + audit/charge.
  const [sessionMismatch, setSessionMismatch] = useState<{
    email: string
  } | null>(null)
  const [sendLinkStatus, setSendLinkStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [sendLinkError, setSendLinkError] = useState<string | null>(null)
  // Fallback email field for the session-mismatch modal when the 409
  // response didn't include an email. Without this, the user is stuck
  // with a disabled "Send link" button and no way to recover (eval P1 #8).
  const [sendLinkManualEmail, setSendLinkManualEmail] = useState('')
  const [showPackModal, setShowPackModal] = useState(false)
  useEffect(() => {
    if (!isDev && localStorage.getItem('pm_radar_used') === 'true')
      setScanUsed(true)
  }, [isDev])

  // Results
  const [toolsRaw, setToolsRaw] = useState<Record<string, ToolState>>(() =>
    Object.fromEntries(
      TOOLS.map(t => [
        t.key,
        {
          status: 'idle' as const,
          data: null,
          error: null,
          startedAt: null,
          finishedAt: null,
        },
      ])
    )
  )

  // Derived tools record: always has an entry for every TOOLS key,
  // even if state is stale from HMR or localStorage history
  const tools = useMemo(() => {
    const defaultState: ToolState = {
      status: 'idle',
      data: null,
      error: null,
      startedAt: null,
      finishedAt: null,
    }
    return Object.fromEntries(
      TOOLS.map(t => [t.key, toolsRaw[t.key] || defaultState])
    ) as Record<ToolKey, ToolState>
  }, [toolsRaw])

  // Wrapper so all existing setTools calls keep working
  const setTools = useCallback(
    (
      updater:
        | Record<ToolKey, ToolState>
        | ((prev: Record<ToolKey, ToolState>) => Record<ToolKey, ToolState>)
    ) => {
      if (typeof updater === 'function') {
        setToolsRaw(prev => {
          const full = Object.fromEntries(
            TOOLS.map(t => [
              t.key,
              prev[t.key] || {
                status: 'idle' as const,
                data: null,
                error: null,
                startedAt: null,
                finishedAt: null,
              },
            ])
          ) as Record<ToolKey, ToolState>
          return updater(full)
        })
      } else {
        setToolsRaw(updater)
      }
    },
    []
  )

  const [logs, setLogs] = useState<ThreadLog[]>([])
  const [expandedTool, setExpandedTool] = useState<ToolKey | null>(null)
  const [showLogs, setShowLogs] = useState(false)
  const lastRunParamsRef = useRef<{
    url: string
    brand: string
    cat: string
  } | null>(null)
  const [activeTab, setActiveTabRaw] = useState<RadarTab>('overview')
  const [actionPlanVisible, setActionPlanVisible] = useState(true)
  // Action plan persists across tab changes. Only an explicit user
  // dismiss (X button on the hero) or a new audit run resets it. The
  // previous behavior auto-hid on first tab change, which made the
  // "top 3 priority fixes shown as hero" promise feel ephemeral.
  const scrollPositions = useRef<Map<typeof activeTab, number>>(new Map())
  const setActiveTab = useCallback(
    (tab: typeof activeTab) => {
      // Cache current scroll for the tab we're leaving so it can be
      // restored on return (eval P5 #24). Default behavior was scroll
      // to top on every switch, losing user position in long tabs.
      if (mainRef.current) {
        scrollPositions.current.set(activeTab, mainRef.current.scrollTop)
      }
      setActiveTabRaw(tab)
    },
    [activeTab]
  )
  const logRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLDivElement>(null)

  // Run history (localStorage)
  const [savedRuns, setSavedRuns] = useState<SavedRun[]>([])
  // Stable identity of the audit currently on screen: set when a run is saved
  // and when a saved run is loaded, cleared when a new audit starts. This is
  // what excludes the current run from its own comparison — never array
  // position, never timestamp equality.
  const [currentRunId, setCurrentRunId] = useState<string | null>(null)
  // Compare selection. `undefined` means the user has not touched Compare yet,
  // in which case the newest run lands with its comparison already open;
  // `null` means they closed it. Derived rather than seeded by an effect so
  // there is no race with the async history load and no open-then-close flash.
  const [compareChoice, setCompareChoice] = useState<string | null | undefined>(
    undefined
  )
  const compareRunId =
    compareChoice === undefined ? (savedRuns[0]?.id ?? null) : compareChoice
  // Inline client-label editor (replaces window.prompt — eval P1 #7).
  // Tracks which run is being edited and the draft value.
  const [editingLabel, setEditingLabel] = useState<{
    runId: string
    value: string
  } | null>(null)

  function persistRuns(updated: SavedRun[]) {
    setSavedRuns(updated)
    try {
      localStorage.setItem('pm_platform_runs', JSON.stringify(updated))
    } catch {
      /* localStorage quota or disabled — silent */
    }
  }

  function commitClientLabel(runId: string, label: string) {
    const trimmed = label.trim()
    persistRuns(
      savedRuns.map(r =>
        r.id === runId ? { ...r, clientLabel: trimmed || undefined } : r
      )
    )
    setEditingLabel(null)
  }

  // Competitor comparison
  const [competitorDomain, setCompetitorDomain] = useState('')
  const [competitorBrand, setCompetitorBrand] = useState('')
  const [competitorCategory, setCompetitorCategory] = useState('')
  const [competitorRunning, setCompetitorRunning] = useState(false)
  const [competitorToolsRaw, setCompetitorToolsRaw] = useState<
    Record<string, ToolState>
  >(() =>
    Object.fromEntries(
      TOOLS.map(t => [
        t.key,
        {
          status: 'idle' as const,
          data: null,
          error: null,
          startedAt: null,
          finishedAt: null,
        },
      ])
    )
  )

  const competitorTools = useMemo(() => {
    const defaultState: ToolState = {
      status: 'idle',
      data: null,
      error: null,
      startedAt: null,
      finishedAt: null,
    }
    return Object.fromEntries(
      TOOLS.map(t => [t.key, competitorToolsRaw[t.key] || defaultState])
    ) as Record<ToolKey, ToolState>
  }, [competitorToolsRaw])

  const setCompetitorTools = useCallback(
    (
      updater:
        | Record<ToolKey, ToolState>
        | ((prev: Record<ToolKey, ToolState>) => Record<ToolKey, ToolState>)
    ) => {
      if (typeof updater === 'function') {
        setCompetitorToolsRaw(prev => {
          const full = Object.fromEntries(
            TOOLS.map(t => [
              t.key,
              prev[t.key] || {
                status: 'idle' as const,
                data: null,
                error: null,
                startedAt: null,
                finishedAt: null,
              },
            ])
          ) as Record<ToolKey, ToolState>
          return updater(full)
        })
      } else {
        setCompetitorToolsRaw(updater)
      }
    },
    []
  )

  // Load saved runs from Supabase (with localStorage fallback) on mount
  useEffect(() => {
    let cancelled = false
    async function loadRuns() {
      try {
        const res = await fetch('/api/platform/audit-runs?limit=50', {
          headers: platformHeaders(),
        })
        if (res.ok) {
          const { runs } = await res.json()
          if (!cancelled && runs?.length) {
            const mapped: SavedRun[] = runs.map((r: any) => ({
              id: r.id,
              domain: r.domain,
              timestamp: r.timestamp,
              unifiedScore: r.unifiedScore,
              unifiedGrade: r.unifiedGrade,
              toolScores: r.toolScores || {},
              toolData: {},
            }))
            setSavedRuns(mapped)
            try {
              localStorage.setItem('pm_platform_runs', JSON.stringify(mapped))
            } catch {
              /* ignore */
            }
            return
          }
        }
      } catch {
        /* Supabase unavailable, fall through to localStorage */
      }

      // Fallback: load from localStorage
      try {
        const stored = localStorage.getItem('pm_platform_runs')
        if (!cancelled && stored) setSavedRuns(JSON.parse(stored))
      } catch {
        /* ignore */
      }
    }
    loadRuns()
    return () => {
      cancelled = true
    }
  }, [])

  // Restore last session from localStorage on mount
  useEffect(() => {
    try {
      const lastSession = localStorage.getItem('pm_platform_session')
      if (lastSession) {
        const session = JSON.parse(lastSession)
        if (session.tools && session.domain && session.selected) {
          // One-time purge: detect-brand previously hallucinated "Pixel Mojo"
          // (two words) for pixelmojo.io. The fix lives in the route's
          // KNOWN_DOMAIN_BRANDS map, but browsers with the stale value in
          // localStorage would keep re-injecting it on restore. Clear the
          // contaminated fields so detect-brand re-runs cleanly.
          if (session.brand === 'Pixel Mojo') {
            session.brand = ''
            session.category = ''
            session.brandKeywords = ''
            session.brandDescription = ''
            session.entity = null
          }
          // Entities resolved before the site-first resolver carry no
          // provenance and must not be treated as verified. Dropping them
          // re-resolves the domain instead of trusting a stale guess.
          if (session.entity && !session.entity.resolverVersion) {
            session.entity = null
          }
          // Restore tools state, marking any new tools (not in saved session) as idle
          const restoredTools = Object.fromEntries(
            TOOLS.map(t => [
              t.key,
              session.tools[t.key] || {
                status: 'idle' as const,
                data: null,
                error: null,
                startedAt: null,
                finishedAt: null,
              },
            ])
          ) as Record<ToolKey, ToolState>
          setAuditGeneration(generation => generation + 1)
          setTools(restoredTools)
          setActiveDomain(session.domain)
          setDomain(session.domainInput || '')
          setBrand(session.brand || '')
          setCategory(session.category || '')
          setBrandKeywords(session.brandKeywords || '')
          setBrandDescription(session.brandDescription || '')
          setBrandDetected(!!session.brand)
          setEntity(session.entity ?? null)
          // Merge saved selection with any new tools added since the session was saved
          const restored = new Set<ToolKey>(session.selected)
          for (const t of TOOLS) {
            if (!restored.has(t.key)) restored.add(t.key)
          }
          setSelected(restored)
        }
      }
    } catch {
      /* ignore */
    }
  }, [])

  const addLog = useCallback(
    (
      tool: ToolKey | 'system',
      message: string,
      type: ThreadLog['type'] = 'info'
    ) => {
      setLogs(prev => [...prev, { time: Date.now(), tool, message, type }])
      setTimeout(
        () =>
          logRef.current?.scrollTo({
            top: logRef.current.scrollHeight,
            behavior: 'smooth',
          }),
        50
      )
    },
    []
  )

  const updateTool = useCallback((key: ToolKey, update: Partial<ToolState>) => {
    setTools(prev => ({ ...prev, [key]: { ...prev[key], ...update } }))
  }, [])

  const toggle = (key: ToolKey) => {
    if (isRunning) return
    setSelected(prev => {
      const n = new Set(prev)
      if (n.has(key)) n.delete(key)
      else n.add(key)
      return n
    })
  }

  // Retry a single failed tool
  async function retryTool(key: ToolKey) {
    if (!lastRunParamsRef.current) return
    const { url, brand: b, cat } = lastRunParamsRef.current
    await runTool(key, url, b, cat)
  }

  async function runTool(
    key: ToolKey,
    urlStr: string,
    brandName: string,
    cat: string,
    sessionId?: string
  ) {
    const tool = TOOLS.find(t => t.key === key)!
    updateTool(key, {
      status: 'running',
      startedAt: Date.now(),
      data: null,
      error: null,
      finishedAt: null,
    })
    addLog(key, `Starting ${tool.name} analysis...`)

    const body: Record<string, string> = {}
    // Every tool gets the audited URL, Reddit included: it is the first-party
    // evidence entity verification needs to tell a real brand mention from a
    // same-name coincidence. Excluding it here is what let unattributable
    // same-name hits count as mentions.
    body.url = urlStr
    if (tool.needsBrand || key === 'citation') body.brand = brandName
    if (
      key === 'citation' ||
      key === 'sourceInfluence' ||
      key === 'promptSov'
    ) {
      body.category = cat
      if (locale !== 'global') body.locale = locale
    }
    if (key === 'reddit') {
      body.brand = brandName
      if (brandKeywords) body.keywords = brandKeywords
    }
    if (key === 'answer') {
      body.question = `What is ${brandName} and what do they specialize in?`
    }

    const toolStartedAt = Date.now()
    try {
      const storedFp = localStorage.getItem('pm_fp')
      const res = await fetch(tool.endpoint, {
        method: 'POST',
        headers: platformHeaders({
          'x-tool-fingerprint': storedFp,
          'x-mlflow-session-id': sessionId,
        }),
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) {
        // Server-side usage limit hit
        if (res.status === 403 && data.error === 'usage_limit' && !isDev) {
          setScanUsed(true)
          localStorage.setItem('pm_radar_used', 'true')
        }
        updateTool(key, {
          status: 'error',
          error: data.message || 'Failed',
          finishedAt: Date.now(),
        })
        addLog(key, `Error: ${data.message || 'Unknown error'}`, 'error')
        radarAnalytics.toolFailed(key, res.status, Date.now() - toolStartedAt)
        return
      }
      updateTool(key, { status: 'done', data, finishedAt: Date.now() })
      const withheld =
        data.scoreStatus === 'withheld' ||
        typeof data.overallScore !== 'number' ||
        typeof data.grade !== 'string'
      addLog(
        key,
        withheld
          ? 'Complete: score withheld (evaluation incomplete)'
          : `Complete: ${data.overallScore}/100 (Grade ${data.grade})`,
        'success'
      )
      radarAnalytics.toolCompleted(
        key,
        withheld ? 'withheld' : data.grade,
        Date.now() - toolStartedAt
      )
    } catch {
      updateTool(key, {
        status: 'error',
        error: 'Network error',
        finishedAt: Date.now(),
      })
      addLog(key, 'Network error', 'error')
      radarAnalytics.toolFailed(key, null, Date.now() - toolStartedAt)
    }
  }

  function handleNewTest() {
    const defaultTools = Object.fromEntries(
      TOOLS.map(t => [
        t.key,
        {
          status: 'idle' as const,
          data: null,
          error: null,
          startedAt: null,
          finishedAt: null,
        },
      ])
    ) as Record<ToolKey, ToolState>
    setTools(defaultTools)
    setAuditGeneration(generation => generation + 1)
    setDomain('')
    setActiveDomain('')
    setBrand('')
    setCategory('')
    setBrandKeywords('')
    setBrandDescription('')
    setBrandDetected(false)
    setSelected(new Set(TOOLS.map(t => t.key)))
    setLogs([])
    setExpandedTool(null)
    setActiveTab('overview')
    setInputError(null)
    setCurrentRunId(null)
    setCompareChoice(null)
    localStorage.removeItem('pm_platform_session')
  }

  async function handleRun(e: React.FormEvent, overrideDomain?: string) {
    e.preventDefault()
    setInputError(null)

    // scanUsed is a localStorage flag set after the anonymous free-tier
    // run. It must only block FREE-tier users. Paid users (credits or
    // subscription) had this flag set during their pre-purchase visits
    // and were getting blocked despite having credits — see Roy
    // Castleman incident 2026-05-07.
    if (scanUsed && userTier === 'free' && !isDev) {
      setInputError(
        'Your scan has been used. Request a new token for another scan.'
      )
      return
    }

    let urlStr = (overrideDomain ?? domain).trim()
    if (!urlStr) {
      setInputError('Enter a domain.')
      return
    }
    if (!urlStr.startsWith('http://') && !urlStr.startsWith('https://'))
      urlStr = `https://${urlStr}`
    try {
      const p = new URL(urlStr)
      if (!p.hostname.includes('.')) {
        setInputError('Enter a valid domain.')
        return
      }
    } catch {
      setInputError('Enter a valid domain.')
      return
    }
    if (selected.size === 0) {
      setInputError('Select at least one tool.')
      return
    }

    // ── Entity resolution + confirmation gate ────────────────────────────────
    // Deliberately BEFORE the credit charge and before any tool dispatch. An
    // entity we are not confident about must not consume a credit, must not
    // reach a paid audit, and must not reach the remediation we hand back.
    // wreb.de was charged, audited as a US dental licensing board, and
    // delivered a D/37 for a company that was never theirs.
    //
    // A value the user already confirmed is authoritative and is NOT
    // re-resolved — automatic resolution never overwrites a human decision.
    let brandName = brand.trim()
    let cat = category.trim()
    let runEntity = entity

    if (!entity?.confirmedByUser && (!brandName || !brandDetected)) {
      setDetectingBrand(true)
      try {
        const res = await fetch('/api/tools/detect-brand', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: urlStr }),
        })
        if (res.ok) {
          const data = await res.json()
          brandName =
            data.brand ||
            new URL(urlStr).hostname.replace('www.', '').split('.')[0]
          cat = data.category || 'technology companies'
          runEntity = {
            domain: entityDomainKey(urlStr),
            brand: brandName,
            legalName: data.legalName ?? null,
            source: data.source ?? 'domain_fallback',
            confidence: data.confidence ?? 'low',
            requiresConfirmation: data.requiresConfirmation !== false,
            evidence: data.evidence ?? [],
            conflicts: data.conflicts ?? [],
            resolverVersion: data.resolverVersion ?? 'unknown',
            resolvedAt: data.resolvedAt ?? new Date().toISOString(),
            detectedBrand: brandName,
            confirmedByUser: false,
          }
          setBrand(brandName)
          setCategory(cat)
          setBrandKeywords(data.keywords || '')
          setBrandDescription(data.description || '')
          setBrandDetected(true)
          setEntity(runEntity)
        }
      } catch (error) {
        // The gate below still fails closed, so the user is never silently
        // run against an unresolved entity — but a resolver outage looks
        // identical to "this site declares nothing" without a diagnostic.
        console.error('[audit] entity resolution failed:', error)
      }
      setDetectingBrand(false)
    }

    if (!brandName)
      brandName = new URL(urlStr).hostname.replace('www.', '').split('.')[0]
    if (!cat) cat = 'technology companies'

    // Fails closed: a resolution that never completed leaves runEntity null,
    // which canRunWithoutConfirmation() rejects.
    if (!canRunWithoutConfirmation(runEntity, urlStr)) {
      setEntityConfirmPending(true)
      setAdvancedOpen(true)
      return
    }

    // Charge 1 credit/quota for this audit run BEFORE starting tools.
    // Anonymous users hit free fingerprint tier downstream.
    // Authenticated users without paid access get blocked here (402).
    let chargeStatus: string | null = null
    try {
      const chargeRes = await fetch('/api/platform/audit/charge', {
        method: 'POST',
        headers: {
          ...platformHeaders(),
          // LLM referral attribution. peek, never mint: unlocking must not
          // create attribution for a visitor who was never LLM-referred.
          ...(peekVisitorId() ? { 'x-pm-visitor-id': peekVisitorId()! } : {}),
        },
      })
      if (chargeRes.status === 402) {
        setShowUpgradeModal(true)
        return
      }
      // 409 session_mismatch: paid state exists but consume missed. The
      // charge route already recorded an access_anomalies row and fired a
      // Sentry alert. Show the recovery modal, not the pay modal.
      if (chargeRes.status === 409) {
        const body = await chargeRes.json().catch(() => null)
        const email =
          typeof body?.email === 'string' && body.email.length > 0
            ? body.email
            : ''
        setSessionMismatch({ email })
        setSendLinkStatus('idle')
        setSendLinkError(null)
        return
      }
      if (!chargeRes.ok) {
        setInputError('Could not start audit. Please try again.')
        return
      }
      const chargeBody = await chargeRes.json()
      chargeStatus = chargeBody?.status ?? null
      // Store the receipt for the upcoming saveRun(). Anonymous users get
      // status='free_anonymous' with no chargeId — saveRun handles that path.
      setActiveChargeId(chargeBody?.chargeId ?? null)
    } catch {
      setInputError('Network error. Please try again.')
      return
    }

    radarAnalytics.auditStarted(userTier)
    auditStartedAtRef.current = Date.now()

    // Free tier (anonymous): skip tier-2 (paid-only) tools even if selected.
    // Tier-2 tool routes also server-side-enforce via requirePaidTier(),
    // but filtering here avoids wasted calls + shows clear skip log to user.
    const isFreeTier = chargeStatus === 'free_anonymous'
    const runnableSelected = new Set(selected)
    const skippedPaidTools: string[] = []
    if (isFreeTier) {
      for (const key of selected) {
        const tool = TOOLS.find(t => t.key === key)
        if (tool && tool.tier > 1) {
          runnableSelected.delete(key)
          skippedPaidTools.push(tool.name)
        }
      }
    }

    setAuditGeneration(generation => generation + 1)
    setActiveDomain(new URL(urlStr).hostname)
    // A fresh audit is not yet any saved run. Clearing here stops a previously
    // loaded run's id from excluding a legitimate comparison target while the
    // new scan is in flight; saveRun() sets the real id on completion.
    setCurrentRunId(null)
    setIsRunning(true)
    // Hand off to the dashboard's running state. Deliberately here and not at
    // submit: everything above this line can still bail (invalid domain, 402
    // needs_credits, 409 session mismatch) and those errors render on the
    // form, so closing earlier would hide them.
    setNewAuditRequested(false)
    setLogs([])
    setExpandedTool(null)
    setActiveTabRaw('overview')
    setActionPlanVisible(true)
    setTools(
      Object.fromEntries(
        TOOLS.map(t => [
          t.key,
          {
            status: runnableSelected.has(t.key)
              ? ('queued' as const)
              : isFreeTier && t.tier > 1 && selected.has(t.key)
                ? ('locked' as const)
                : ('skipped' as const),
            data: null,
            error: null,
            startedAt: null,
            finishedAt: null,
          },
        ])
      ) as Record<ToolKey, ToolState>
    )

    addLog(
      'system',
      `Hive multi-agent audit initialized for ${new URL(urlStr).hostname}`
    )

    if (isFreeTier && skippedPaidTools.length > 0) {
      addLog(
        'system',
        `Paid-only tools skipped (upgrade to unlock): ${skippedPaidTools.join(', ')}`,
        'error'
      )
    }

    addLog(
      'system',
      `Entity: ${brandName} (${runEntity?.source ?? 'unconfirmed'}, ${runEntity?.confidence ?? 'unknown'} confidence${runEntity?.confirmedByUser ? ', user-confirmed' : ''})`,
      'success'
    )

    lastRunParamsRef.current = { url: urlStr, brand: brandName, cat }

    // Stagger lightweight tools in batches of 3. Provider-intensive tools run
    // standalone so one heavy tool settles before the next one starts.
    // runnableSelected excludes tier-2 tools for free-tier users.
    const selectedTools = TOOLS.filter(t => runnableSelected.has(t.key))
    const toolBatches = planToolBatches(selectedTools, 3)

    // Shared session ID so all tool_runs from this audit can be correlated.
    const auditSessionId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `audit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

    await runSequentialToolBatches(
      toolBatches,
      tool => runTool(tool.key, urlStr, brandName, cat, auditSessionId),
      (batch, index) =>
        addLog(
          'system',
          index === 0
            ? `Dispatching ${selectedTools.length} tool${selectedTools.length > 1 ? 's' : ''} (batch 1: ${batch.map(t => t.name).join(', ')})`
            : `Dispatching batch ${index + 1}: ${batch.map(t => t.name).join(', ')}`
        )
    )

    addLog('system', 'Generating cross-tool analysis...', 'info')
    await new Promise(r => setTimeout(r, 300))
    if (!isDev) {
      setScanUsed(true)
      localStorage.setItem('pm_radar_used', 'true')
    }
    // radar_audit_completed fires from the effect below once the final
    // tools state and unified score are rendered — not here, where the
    // closure over `tools` is stale.
    auditCompletePendingRef.current = true
    setIsRunning(false)
    addLog('system', 'Audit complete', 'success')
  }

  // Save current run to history (Supabase primary, localStorage backup)
  const [isSaving, setIsSaving] = useState(false)
  const shareFlash = useSuccessFlash()
  async function saveRun() {
    if (doneTools.length === 0 || !activeDomain || isSaving) return
    // A withheld-only run has no score to save. `SavedRun.unifiedScore` is
    // typed `number`, so persisting one meant writing 0 — a real F baseline
    // for an audit that never graded anything, which a later run then reads
    // as a large improvement. Nothing gradeable means nothing to record.
    if (unifiedScore === null) return
    setIsSaving(true)

    const toolScores: Record<string, { score: number; grade: string }> = {}
    const toolData: Record<string, any> = {}
    doneTools.forEach(([key, s]) => {
      // `doneTools` includes withheld tools: they completed, they just
      // declined to grade, so `overallScore` is null. Only usable scores are
      // persisted — a withheld check written as a number is indistinguishable
      // from a measured one on every later read (see failingCheckCount).
      const score = usableScore(s)
      if (score !== null) toolScores[key] = { score, grade: s.data.grade }
      // toolData keeps every completed tool, withheld included: it is the
      // full payload the detail panels re-render, not a score.
      toolData[key] = s.data
    })

    // Save to Supabase first (source of truth)
    let supabaseId: string | null = null
    try {
      const res = await fetch('/api/platform/audit-runs', {
        method: 'POST',
        headers: platformHeaders(),
        body: JSON.stringify({
          domain: activeDomain,
          brand,
          category,
          unifiedScore,
          unifiedGrade,
          toolScores,
          toolData,
          chargeId: activeChargeId,
          // Provenance of the entity this run actually measured. Without it we
          // cannot tell later whether a poor brand-dependent score was a real
          // finding or our own resolver supplying the wrong company.
          entity,
        }),
      })
      if (res.ok) {
        const result = await res.json()
        supabaseId = result.id
        // Single-use receipt: clear so the same charge can't be re-saved if
        // the user clicks Save / hits S after the auto-save completes.
        setActiveChargeId(null)
        addLog('system', `Run saved to database (${activeDomain})`, 'success')
      } else {
        const err = await res.json().catch(() => ({}))
        console.error('[SaveRun] Supabase error:', err)
        addLog(
          'system',
          'Run saved locally only (database unavailable)',
          'error'
        )
      }
    } catch (err) {
      console.error('[SaveRun] Network error:', err)
      addLog('system', 'Run saved locally only (network error)', 'error')
    }

    radarAnalytics.reportSaved(supabaseId ? 'database' : 'local_only')

    const run: SavedRun = {
      id: supabaseId || `run-${Date.now()}`,
      domain: activeDomain,
      timestamp: Date.now(),
      // Not `?? 0`: the withheld-only case returned early above, so this is
      // always a real measured score. The old coercion wrote a fabricated 0.
      unifiedScore,
      unifiedGrade,
      toolScores,
      toolData,
    }
    // The completed run is now its own first history entry, so record its id.
    // Without this every comparison below would diff the run against itself.
    setCurrentRunId(run.id)
    const updated = [run, ...savedRuns].slice(0, 50)
    setSavedRuns(updated)

    // Mirror to localStorage as backup
    try {
      localStorage.setItem('pm_platform_runs', JSON.stringify(updated))
    } catch {
      /* ignore */
    }

    setIsSaving(false)
  }

  async function deleteRun(id: string) {
    const updated = savedRuns.filter(r => r.id !== id)
    setSavedRuns(updated)
    if (compareRunId === id) setCompareChoice(null)
    try {
      localStorage.setItem('pm_platform_runs', JSON.stringify(updated))
    } catch {
      /* ignore */
    }

    // Delete from Supabase
    try {
      await fetch(`/api/platform/audit-runs?id=${id}`, {
        method: 'DELETE',
        headers: platformHeaders(),
      })
    } catch {
      console.error('[DeleteRun] Failed to delete from Supabase:', id)
    }
  }

  // Load a saved run back into the dashboard
  async function loadRun(run: SavedRun) {
    let toolDataToUse = run.toolData

    // If toolData is empty (loaded from Supabase listing), fetch full data
    if (!toolDataToUse || Object.keys(toolDataToUse).length === 0) {
      try {
        const res = await fetch(`/api/platform/audit-runs?id=${run.id}`, {
          headers: platformHeaders(),
        })
        if (res.ok) {
          const { run: fullRun } = await res.json()
          if (fullRun?.toolData) {
            toolDataToUse = fullRun.toolData
            // Update the savedRuns array with full data so subsequent loads are instant
            setSavedRuns(prev =>
              prev.map(r =>
                r.id === run.id ? { ...r, toolData: toolDataToUse } : r
              )
            )
          }
        }
      } catch {
        /* fall through with empty toolData */
      }
    }

    setAuditGeneration(generation => generation + 1)
    setDomain(run.domain)
    setActiveDomain(run.domain)

    const restored = Object.fromEntries(
      TOOLS.map(t => {
        const saved = toolDataToUse[t.key]
        if (saved) {
          return [
            t.key,
            {
              status: 'done' as const,
              data: saved,
              error: null,
              startedAt: run.timestamp,
              finishedAt: run.timestamp,
            },
          ]
        }
        return [
          t.key,
          {
            status: 'idle' as const,
            data: null,
            error: null,
            startedAt: null,
            finishedAt: null,
          },
        ]
      })
    ) as Record<ToolKey, ToolState>

    setTools(restored)
    setCurrentRunId(run.id)
    setActiveTab('overview')
    setExpandedTool(null)
  }

  // Export report as JSON
  function exportJSON() {
    // Never serialize a half-complete action set. Readiness is derived
    // from the CURRENT audit key, not a pending flag, so there is no render
    // in which this reads ready while holding another audit's enrichment.
    if (!gscReady) return
    if (doneTools.length === 0) return
    const report = {
      domain: activeDomain,
      timestamp: new Date().toISOString(),
      unifiedScore,
      unifiedGrade,
      tools: Object.fromEntries(
        doneTools.map(([key, s]) => [
          key,
          {
            score: s.data.overallScore,
            grade: s.data.grade,
            aiSummary: s.data.aiSummary,
            recommendations: s.data.recommendations,
          },
        ])
      ),
      insights: insights.map(i => ({
        type: i.type,
        title: i.title,
        message: i.message,
      })),
      // Which ordering methodology produced this list. Separate from the
      // composite score's method version, which T4 did not change.
      actionMethodVersion: RADAR_ACTION_METHOD_VERSION,
      // Canonical order at the boundary, and no `impact` field: it is a
      // hand-authored severity with no measurement behind it, so exporting
      // it invited downstream consumers to treat it as evidence.
      actions: sortActions(actions).map(a => ({
        title: a.title,
        category: a.category,
        effort: a.effort,
        priorityScore: a.priorityDetail?.priorityScore ?? null,
        prioritySource: a.priorityDetail?.prioritySource ?? 'unranked',
        // Absent stays absent: the contract is `findingType?: FindingType`,
        // and JSON.stringify omits undefined. Emitting `?? null` would put a
        // value on the wire that is not in the union and would contradict
        // the "never guess for an unclassified source" rule.
        findingType: a.findingType,
        why: a.why,
        how: a.how,
      })),
    }
    const blob = new Blob([JSON.stringify(report, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai-visibility-report-${activeDomain}-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
    radarAnalytics.reportExported('json')
  }

  // Export as printable PDF (opens print dialog)
  function exportPDF() {
    // Same readiness gate as exportJSON — see there.
    if (!gscReady) return
    if (doneTools.length === 0) return

    // XSS protection: escape all user-controlled content before inserting into HTML
    function esc(str: string): string {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }

    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    const gradeColor = (g: string) =>
      ({
        A: 'var(--growth)',
        B: 'var(--primary)',
        C: 'var(--warning-text)',
        D: '#f97316',
        F: 'var(--cta)',
      })[g] || '#666'
    const safeDomain = esc(activeDomain || '')

    const toolRows = doneTools
      .map(([key, s]) => {
        const t = TOOLS.find(t2 => t2.key === key)!
        return `<tr><td style="padding:8px;border-bottom:1px solid #eee">${esc(t.name)}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:center;font-weight:700;color:${gradeColor(s.data.grade)}">${hasUsableScore(s) ? `${s.data.overallScore}/100 (${esc(s.data.grade)})` : 'withheld'}</td><td style="padding:8px;border-bottom:1px solid #eee;font-size:12px;color:#666">${esc(s.data.aiSummary?.summary || 'No AI summary available.')}</td></tr>`
      })
      .join('')

    const insightRows = insights
      .map(i => {
        const icon =
          i.type === 'good'
            ? '&#10003;'
            : i.type === 'conflict'
              ? '&#9888;'
              : '&#10007;'
        const color =
          i.type === 'good'
            ? 'var(--growth)'
            : i.type === 'conflict'
              ? '#f97316'
              : 'var(--cta)'
        return `<div style="display:flex;gap:8px;margin-bottom:12px"><span style="color:${color};font-size:18px">${icon}</span><div><strong>${esc(i.title)}</strong><br><span style="color:#666;font-size:13px">${esc(i.message)}</span></div></div>`
      })
      .join('')

    // Canonical sort applied AT the serialization boundary rather than
    // trusting whatever order the caller happened to pass. generateActions
    // already sorts, but this export must not depend on that accident — a
    // future persisted snapshot or API path could supply an unsorted array.
    const actionRows = sortActions(actions)
      .slice(0, 10)
      .map((a, i) => {
        return `<div style="margin-bottom:16px;padding:12px;border:1px solid #eee;border-radius:8px"><div style="display:flex;gap:8px;align-items:center;margin-bottom:4px"><span style="background:#f0f0f0;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700">#${i + 1}</span><span style="font-size:11px;color:${EFFORT_LABELS[a.effort].color}">${esc(EFFORT_LABELS[a.effort].label)}</span></div><strong style="font-size:14px">${esc(a.title)}</strong><p style="margin:6px 0 4px;font-size:12px;color:#666"><em>Why:</em> ${esc(a.why)}</p><p style="margin:0;font-size:12px;color:#666"><em>How:</em> ${esc(a.how)}</p></div>`
      })
      .join('')

    const html = `<!DOCTYPE html><html><head><title>AI Visibility Report - ${safeDomain}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#1a1a1a}h1{font-size:24px;margin-bottom:4px}h2{font-size:18px;margin-top:32px;padding-bottom:8px;border-bottom:2px solid #008dcc}table{width:100%;border-collapse:collapse}th{text-align:left;padding:8px;border-bottom:2px solid #333;font-size:13px;text-transform:uppercase;letter-spacing:0.5px}@media print{body{padding:20px}}</style></head><body><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:32px"><div><h1>AI Visibility Report</h1><p style="color:#666;margin:0">${safeDomain} &mdash; ${esc(date)}</p></div><div style="text-align:center"><div style="width:80px;height:80px;border-radius:50%;border:4px solid ${gradeColor(unifiedGrade)};display:flex;align-items:center;justify-content:center;margin:0 auto"><span style="font-size:28px;font-weight:900;color:${gradeColor(unifiedGrade)}">${unifiedScore ?? '--'}</span></div><div style="font-size:12px;font-weight:700;color:${gradeColor(unifiedGrade)};margin-top:4px">Grade ${esc(unifiedGrade)}</div></div></div><h2>Tool Scores</h2><table><thead><tr><th>Tool</th><th style="text-align:center">Score</th><th>AI Summary</th></tr></thead><tbody>${toolRows}</tbody></table>${insights.length > 0 ? `<h2>Cross-Tool Insights</h2>${insightRows}` : ''}${actions.length > 0 ? `<h2>Priority Action Items</h2>${actionRows}` : ''}<div style="margin-top:40px;padding-top:16px;border-top:1px solid #eee;font-size:11px;color:#999;text-align:center">Generated by Pixelmojo Radar</div></body></html>`

    // Print via hidden iframe (no window.open, no redirect)
    let printFrame = document.getElementById(
      'pm-print-frame'
    ) as HTMLIFrameElement | null
    if (!printFrame) {
      printFrame = document.createElement('iframe')
      printFrame.id = 'pm-print-frame'
      printFrame.style.cssText =
        'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;'
      document.body.appendChild(printFrame)
    }
    printFrame.srcdoc = html
    printFrame.onload = () => {
      printFrame?.contentWindow?.print()
    }
    radarAnalytics.reportExported('pdf')
  }

  // Wire keyboard shortcut refs
  exportPDFRef.current = exportPDF
  saveRunRef.current = saveRun
  loadRunRef.current = loadRun

  // Generate shareable public report link (Supabase-backed, viewable without account).
  // Success feedback is driven by useSuccessFlash → <Button success={shareFlash.success}>
  // which renders the checkmark + "Link copied!" label inside an aria-live region.
  async function shareReport() {
    if (doneTools.length === 0 || !activeDomain) return

    const toolScores: Record<string, { score: number; grade: string }> = {}
    const toolData: Record<string, unknown> = {}
    doneTools.forEach(([key, s]) => {
      const score = usableScore(s)
      if (score !== null) toolScores[key] = { score, grade: s.data.grade }
      toolData[key] = s.data
    })

    const fallbackShare = async () => {
      const encoded = btoa(
        JSON.stringify({ d: activeDomain, s: { u: unifiedScore } })
      )
      const url = `${window.location.origin}/platform?r=${encoded}`
      await navigator.clipboard.writeText(url)
      shareFlash.trigger()
      radarAnalytics.reportShared('fallback')
    }

    try {
      const res = await fetch('/api/platform/share-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: activeDomain,
          brand,
          unifiedScore,
          unifiedGrade,
          toolScores,
          toolData,
        }),
      })
      const data = await res.json()
      if (data.shareUrl) {
        await navigator.clipboard.writeText(data.shareUrl)
        shareFlash.trigger()
        radarAnalytics.reportShared('share_url')
      } else {
        await fallbackShare()
      }
    } catch {
      await fallbackShare()
    }
  }

  // Run competitor audit
  async function runCompetitor(e: React.FormEvent) {
    e.preventDefault()
    let urlStr = competitorDomain.trim()
    if (!urlStr) return
    if (!urlStr.startsWith('http://') && !urlStr.startsWith('https://'))
      urlStr = `https://${urlStr}`
    try {
      new URL(urlStr)
    } catch {
      return
    }

    const brandName =
      competitorBrand.trim() ||
      new URL(urlStr).hostname.replace('www.', '').split('.')[0]
    const cat = competitorCategory.trim() || 'technology companies'

    setCompetitorRunning(true)
    setCompetitorTools(
      Object.fromEntries(
        TOOLS.map(t => [
          t.key,
          {
            status: selected.has(t.key)
              ? ('queued' as const)
              : ('skipped' as const),
            data: null,
            error: null,
            startedAt: null,
            finishedAt: null,
          },
        ])
      ) as Record<ToolKey, ToolState>
    )

    const updateCompTool = (key: ToolKey, update: Partial<ToolState>) => {
      setCompetitorTools(prev => ({
        ...prev,
        [key]: { ...prev[key], ...update },
      }))
    }

    const competitorSessionId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `comp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

    async function runCompTool(key: ToolKey) {
      const tool = TOOLS.find(t => t.key === key)!
      updateCompTool(key, {
        status: 'running',
        startedAt: Date.now(),
        data: null,
        error: null,
        finishedAt: null,
      })
      const body: Record<string, string> = {}
      // Same contract as the primary dispatcher: the competitor URL is the
      // first-party evidence Reddit entity verification runs against.
      body.url = urlStr
      if (tool.needsBrand || key === 'citation') body.brand = brandName
      if (
        key === 'citation' ||
        key === 'sourceInfluence' ||
        key === 'promptSov'
      ) {
        body.category = cat
        if (locale !== 'global') body.locale = locale
      }
      if (key === 'reddit') body.brand = brandName
      try {
        const res = await fetch(tool.endpoint, {
          method: 'POST',
          headers: platformHeaders({
            'x-mlflow-session-id': competitorSessionId,
          }),
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (!res.ok) {
          updateCompTool(key, {
            status: 'error',
            error: data.message || 'Failed',
            finishedAt: Date.now(),
          })
          return
        }
        updateCompTool(key, { status: 'done', data, finishedAt: Date.now() })
      } catch {
        updateCompTool(key, {
          status: 'error',
          error: 'Network error',
          finishedAt: Date.now(),
        })
      }
    }

    const compSelected = TOOLS.filter(t => selected.has(t.key))
    const compBatch1 = compSelected.slice(0, 3)
    const compBatch2 = compSelected.slice(3)
    await Promise.allSettled(compBatch1.map(t => runCompTool(t.key)))
    if (compBatch2.length > 0) {
      await Promise.allSettled(compBatch2.map(t => runCompTool(t.key)))
    }
    setCompetitorRunning(false)
  }

  // Computed values
  const doneTools = Object.entries(tools).filter(
    ([, s]) => s.status === 'done' && s.data
  ) as [ToolKey, ToolState][]
  // Withheld tools are EXCLUDED, never coerced to 0. `unifiedScoreAgg.score`
  // is null when nothing was gradeable; the UI renders that as "not enough
  // data to score" rather than a number nobody earned.
  const unifiedScoreAgg = aggregateToolScores(tools)
  // Deliberately NOT `?? 0`. A withheld-only run has no score, and coercing
  // it here would hand every downstream consumer a real-looking 0 — the exact
  // bug this change set exists to remove, reintroduced one layer up.
  const unifiedScore: number | null = unifiedScoreAgg.score
  const hasUnifiedScore = unifiedScore !== null
  const unifiedGrade = gradeFromScore(unifiedScore ?? 0)
  const hasResults =
    doneTools.length > 0 || Object.values(tools).some(s => s.status === 'error')
  const insights = hasResults && !isRunning ? generateInsights(tools) : []
  // ── GSC join findings (optional enrichment) ───────────────────────────
  // Fetched from the server because the checks read stored GSC rows through
  // the service-role client and cannot run in this bundle. Optional by
  // design: any failure leaves the audit exactly as it was.
  // Fetch identity is the normalized activeDomain. Merge ownership is the
  // results generation, so a late same-domain response cannot cross audits.
  // Neither editable `domain` nor autosave's currentRunId participates.
  const { actions: gscActions, ready: gscReady } = useGscEnrichment({
    activeDomain,
    hasCompletedResults: hasResults && !isRunning,
    auditGeneration,
  })

  // Memoized so the canonical merge below can depend on it directly. It
  // used to be recomputed every render, which is why that memo carried an
  // exhaustive-deps suppression listing the inputs by hand.
  const baseActions = useMemo(
    () => (hasResults && !isRunning ? generateActions(tools) : []),
    [tools, hasResults, isRunning]
  )
  /**
   * THE canonical action array. Every consumer below reads this one —
   * panel, plan, dashboard, insights tab, roadmap, threads, both exports —
   * so a GSC action cannot reach one surface and miss another.
   *
   * Merged then sorted through the T4 comparator, never concatenated after
   * a sort: appending to a sorted array would put GSC actions at the end
   * regardless of their tier.
   */
  const actions = useMemo(
    () => mergeCanonicalActions(baseActions, gscActions),
    [baseActions, gscActions]
  )
  const threads = hasResults && !isRunning ? buildThreads(actions, tools) : []

  // radar_audit_completed: fires exactly once per run (pending ref set at
  // the end of handleRun), on the first render where the final tools state
  // and unified score are available.
  const failedToolCount = Object.values(tools).filter(
    s => s.status === 'error'
  ).length
  const doneToolCount = doneTools.length
  useEffect(() => {
    if (isRunning || !auditCompletePendingRef.current) return
    auditCompletePendingRef.current = false
    radarAnalytics.auditCompleted({
      userTier,
      score: unifiedScore,
      grade: unifiedGrade,
      successfulToolCount: doneToolCount,
      failedToolCount,
      durationMs: Date.now() - (auditStartedAtRef.current ?? Date.now()),
    })
  }, [
    isRunning,
    userTier,
    unifiedScore,
    unifiedGrade,
    doneToolCount,
    failedToolCount,
  ])

  // Restore the cached scroll position for the tab we're switching to
  // (defaults to 0 on first visit). Pairs with the scroll capture in
  // setActiveTab. Uses behavior: 'auto' because animating a restore
  // feels disorienting when returning to a known position.
  useEffect(() => {
    const saved = scrollPositions.current.get(activeTab) ?? 0
    mainRef.current?.scrollTo({ top: saved, behavior: 'auto' })
  }, [activeTab])

  // Thread progress (localStorage)
  const [threadProgress, setThreadProgress] = useState<ProgressMap>({})
  useEffect(() => {
    if (domain) setThreadProgress(loadProgress(domain))
  }, [domain])

  // Win Log (localStorage)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [winLog, setWinLog] = useState<WinLogEntry[]>([])
  useEffect(() => {
    setWinLog(loadWinLog())
  }, [])
  const handleMarkAsWin = useCallback(
    (wins: { provider: string; description: string }[]) => {
      for (const w of wins) {
        const updated = addWin({
          domain: activeDomain || domain,
          provider: w.provider,
          date: Date.now(),
          type: 'citation_flip',
          description: w.description,
        })
        setWinLog(updated)
      }
    },
    [activeDomain, domain]
  )
  const handleToggleComplete = useCallback(
    (threadId: string, stepOrder: number) => {
      setThreadProgress(prev => {
        const next = toggleStepComplete(prev, domain, threadId, stepOrder)
        saveProgress(domain, next)
        return next
      })
    },
    [domain]
  )

  // Competitor computed
  const compDoneTools = Object.entries(competitorTools).filter(
    ([, s]) => s.status === 'done' && s.data
  ) as [ToolKey, ToolState][]
  const compAgg = aggregateToolScores(competitorTools)
  const compScores = compAgg.scored.map(s => s.score)
  const compUnifiedScore = compAgg.score ?? 0
  const compUnifiedGrade = gradeFromScore(compUnifiedScore)

  // Persist session to localStorage whenever tools state changes (after audit completes)
  const trendSavedRef = useRef(false)
  useEffect(() => {
    if (isRunning || !hasResults || !activeDomain) return
    try {
      localStorage.setItem(
        'pm_platform_session',
        JSON.stringify({
          tools,
          domain: activeDomain,
          domainInput: domain,
          brand,
          category,
          brandKeywords,
          brandDescription,
          // Carry the entity so a refresh cannot silently drop a user's
          // confirmation and re-gate (or worse, re-resolve) an entity they
          // already decided on.
          entity,
          selected: [...selected],
        })
      )
    } catch {
      /* ignore */
    }

    // Auto-save trend data (once per audit run).
    //
    // This is the ONLY writer of citation_monitor_history, the table behind
    // the score-trend chart. It is a different table from radar_audit_runs,
    // which backs the History tab — so when this write fails, History keeps
    // filling up while the trend chart stays permanently empty. That is
    // exactly how a domain accumulated dozens of runs and still showed
    // "no history yet".
    //
    // `unifiedScore === null` means every completed tool withheld its grade.
    // The comment below used to say we skip persisting those; we did not — we
    // sent `null`, and citation_monitor_history.overall_score is NOT NULL
    // (migration 038), so the insert was rejected. Skip for real.
    if (
      !trendSavedRef.current &&
      doneTools.length > 0 &&
      unifiedScore !== null
    ) {
      trendSavedRef.current = true
      const citationData = tools.citation?.data
      const sovData = tools.promptSov?.data
      // A numeric `unifiedScore` only proves SOME tool graded. Citation Check
      // can still be withheld, and its per-provider rates are then unearned —
      // a provider with 1 usable response out of 4 would persist as 100%.
      // Gated on the same predicate as the Prompt SOV block below.
      const citationTrend = buildCitationTrendFields(citationData)
      fetch('/api/platform/trend-data', {
        method: 'POST',
        // Authenticated route. Without the platform token this 401s for
        // token-only users and the snapshot is silently dropped, which is
        // why their trend chart stayed empty forever.
        headers: platformHeaders(),
        body: JSON.stringify({
          domain: activeDomain,
          brand,
          category,
          // Guarded by the `unifiedScore !== null` check above: a
          // withheld-only run is never persisted, so a future trend chart
          // cannot render it as a real decline.
          overallScore: unifiedScore,
          grade: unifiedGrade,
          providerScores: citationTrend.providerScores,
          sentimentBreakdown: citationTrend.sentimentBreakdown,
          sovData:
            sovData?.scoreStatus !== 'withheld' &&
            typeof sovData?.overallScore === 'number' &&
            sovData?.targetSOV
              ? {
                  sovPercent: sovData.targetSOV.sovPercent,
                  rank: sovData.targetSOV.rank,
                }
              : {},
          narrativeSummaries: citationData?.narrativeSummaries || [],
          // Only gradeable tools are persisted. Writing a withheld tool as 0
          // would bake the bug into trend history permanently.
          toolScores: Object.fromEntries(
            unifiedScoreAgg.scored.map(s => [s.key, s.score])
          ),
        }),
      })
        .then(async res => {
          if (res.ok) return
          // Not fatal to the audit, but it must not be invisible: a silent
          // failure here is indistinguishable from "this domain has no
          // history", and that is what it looked like for months.
          const body = await res.json().catch(() => null)
          console.error(
            '[TrendData] snapshot not saved:',
            res.status,
            body?.message ?? ''
          )
          addLog(
            'system',
            'Score trend snapshot could not be saved for this run.',
            'error'
          )
        })
        .catch(err => {
          console.error('[TrendData] snapshot request failed:', err)
        })
    }
  }, [
    tools,
    isRunning,
    hasResults,
    activeDomain,
    domain,
    brand,
    category,
    brandKeywords,
    brandDescription,
    selected,
    doneTools,
    unifiedScore,
    unifiedGrade,
  ])

  // Reset trend saved flag when starting a new audit
  const wasRunningRef = useRef(false)
  useEffect(() => {
    if (isRunning) {
      trendSavedRef.current = false
      wasRunningRef.current = true
    }
  }, [isRunning])

  // Auto-save audit to Supabase when run completes
  useEffect(() => {
    if (
      wasRunningRef.current &&
      !isRunning &&
      doneTools.length > 0 &&
      activeDomain
    ) {
      wasRunningRef.current = false
      saveRun()
    }
  }, [isRunning, doneTools.length, activeDomain]) // eslint-disable-line react-hooks/exhaustive-deps

  // Prospect readiness score
  const toolScoresMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const [key, s] of Object.entries(tools)) {
      // usableScore() is the gate: a withheld tool returns null and is left
      // out of the map entirely. Writing the key made SmartCTA count it as
      // "ran" (`null !== undefined`) and its `?? 0` fallbacks turned a refusal
      // to grade into a real 0 — the same withheld-as-zero bug this dashboard
      // fixes everywhere else.
      const score = usableScore(s)
      if (score !== null) map[key] = score
    }
    return map
  }, [tools])

  // The audit this run is compared against: same canonical domain, never the
  // run on screen, newest of what remains. null when there is no prior run for
  // this domain, which hides every movement figure rather than showing +-0.
  const previousRun = useMemo(
    () => findPreviousRun(savedRuns, activeDomain, currentRunId),
    [savedRuns, activeDomain, currentRunId]
  )

  // Audit context string for advisor chat
  const auditContext = useMemo(() => {
    if (!hasResults) return ''
    const parts = [
      `Domain: ${activeDomain}`,
      unifiedScore === null
        ? 'Unified Score: not scored (every completed check withheld its grade)'
        : `Unified Score: ${unifiedScore}/100 (${unifiedGrade})`,
    ]
    for (const [key, s] of Object.entries(tools)) {
      if (s.status === 'done' && s.data) {
        const score = usableScore(s)
        parts.push(
          score === null
            ? `${key}: score withheld (evaluation incomplete)`
            : `${key}: ${score}/100 (${s.data.grade})`
        )
      }
    }
    if (insights.length > 0)
      parts.push(`Key insights: ${insights.map(i => i.title).join('; ')}`)
    return parts.join('\n')
  }, [hasResults, activeDomain, unifiedScore, unifiedGrade, tools, insights])

  // Dynamic suggested questions for advisor chat
  const advisorSuggestedQuestions = useMemo(
    () =>
      hasResults && !isRunning
        ? generateSuggestedQuestions(tools, insights)
        : [],
    [hasResults, isRunning, tools, insights]
  )

  /**
   * The new-audit view owns the screen when the user asked for it, or when
   * there is nothing else to show (first run). A scan in flight keeps it
   * mounted so the submit button can act as the progress indicator; once the
   * run finishes, `newAuditRequested` is cleared by the effect below and the
   * dashboard results take over.
   */
  const showNewAudit =
    activeTab === 'overview' &&
    !openingRun &&
    (newAuditRequested || (!hasResults && !isRunning))

  const openNewAudit = useCallback(() => {
    setActiveTab('overview')
    setNewAuditRequested(true)
    setInputError(null)
    mainRef.current?.scrollTo({ top: 0, behavior: 'auto' })
  }, [setActiveTab])

  /** Back out without touching the audit currently on screen. */
  const closeNewAudit = useCallback(() => {
    setNewAuditRequested(false)
    setInputError(null)
  }, [])

  // Loading: session/tier check in flight.
  if (accessLoading) {
    return (
      <div
        data-surface='radar-dashboard'
        className='flex min-h-screen items-center justify-center bg-surface-app'
      >
        <Loader2 size={24} className='animate-spin text-muted-foreground' />
      </div>
    )
  }

  // Gate: paid access. Free-tier visitors convert here.
  // UNCHANGED BEHAVIOUR — this is the paywall, restyled only. Paid users
  // (session cookie or valid token) are granted above and never see it.
  if (!accessGranted) {
    return (
      <div
        data-surface='radar-dashboard'
        className='min-h-screen bg-surface-app text-foreground'
      >
        <header className='flex items-center justify-between border-b border-hairline px-6 py-3.5'>
          <Link href='/' target='_blank' className='flex items-center gap-2'>
            <RadarLockup />
          </Link>
          <div className='flex items-center gap-4'>
            <a
              href='https://www.pixelmojo.io/platform#pricing'
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs text-muted-foreground hover:underline'
            >
              Pricing
            </a>
            <SignInLink className='text-xs text-muted-foreground hover:underline' />
            <AnimatedThemeToggle showScrollTooltip={false} />
          </div>
        </header>

        <section className='mx-auto max-w-2xl px-6 pb-16 pt-20 text-center'>
          <InstrumentLabel className='justify-center'>
            Radar access
          </InstrumentLabel>
          {/* Same treatment as every other top-level headline on the Radar
              surface (/platform, /platform/sample-report, /products): the
              payoff phrase carries SCORE_TEXT_GRADIENT with the one-time
              sweep. Here the payoff is the price. */}
          <h1 className='mt-5 font-display text-[39px] font-bold leading-tight tracking-[-0.02em]'>
            Unlock Radar:{' '}
            <span
              className='score-gradient-sweep'
              style={{
                backgroundImage: SCORE_TEXT_GRADIENT,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              $5 per audit
            </span>
          </h1>
          <p className='mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground'>
            One $5 audit runs all 13 AI visibility tools across ChatGPT, Claude,
            Gemini, and Perplexity, with cross-tool insights and a ranked fix
            list. Credits never expire, and you can step up to weekly monitoring
            whenever you need it.
          </p>

          {/* Rhythm: 20px eyebrow-to-headline and headline-to-lede, then a
              40px break before the ask, then 24px to the secondary links.
              The two link lines are one group with a single gap rather than
              mt-4/mt-2, which read as one blob at 8px apart. */}
          <div className='mx-auto mt-10 max-w-md'>
            {tokenError && (
              <p
                role='alert'
                className='mb-3 border px-4 py-3 text-xs'
                style={{
                  borderColor:
                    'color-mix(in srgb, var(--cta) 40%, transparent)',
                  color: 'var(--cta)',
                }}
              >
                {tokenError}
              </p>
            )}
            <Button
              type='button'
              variant='cta'
              size='lg'
              fullWidth
              onClick={() => void openCheckout('pack-single', 'gate')}
            >
              Get full access for $5
            </Button>
            <div className='mt-6 flex flex-col gap-2.5 text-[11px] text-muted-foreground'>
              <p>
                Want a free look first?{' '}
                <Link href='/platform' className='text-primary hover:underline'>
                  Check your brand on the free teaser
                </Link>
              </p>
              <p>
                Already have a paid plan?{' '}
                <SignInLink
                  className='text-primary hover:underline'
                  signedOutLabel='Sign in'
                  signedInLabel='My Account'
                />
              </p>
            </div>
          </div>
        </section>
      </div>
    )
  }

  const navSections = buildNavModel({
    actionCount: actions.length,
    savedRunCount: savedRuns.length,
    threadCount: threads.length,
  })

  const statusLine =
    activeDomain && hasResults
      ? `${doneTools.length}/${selected.size} tools${
          savedRuns.length > 0
            ? ` · last audit ${relativeScanLabel(savedRuns[0].timestamp)}`
            : ''
        }`
      : null

  return (
    <>
      <h1 className='sr-only'>Radar</h1>
      {/* Print styles. The dashboard chrome, the audit form and every control
          are dropped, and the always-dark surface is forced back to paper. */}
      <style>{`
      @media print {
        header, footer, aside, [data-advisor-chat], [data-audit-form],
        button, .no-print { display: none !important; }
        [data-surface='radar-dashboard'] { display: block !important; }
        [data-surface='radar-dashboard'] {
          --background: #ffffff !important;
          --foreground: #1a1a1a !important;
          --card: #f8f9fa !important;
          --muted: #e5e7eb !important;
          --muted-foreground: #666666 !important;
          --border: #e5e7eb !important;
          --pm-bg-0: #ffffff !important;
          --pm-bg-1: #ffffff !important;
          --pm-surface-card: #f8f9fa !important;
          --pm-surface-raised: #f1f3f5 !important;
          --pm-surface-inset: #e9ecef !important;
          --pm-hairline: #e5e7eb !important;
          --pm-hairline-strong: #d1d5db !important;
          --pm-text-primary: #1a1a1a !important;
          --pm-text-secondary: #4b5563 !important;
          --pm-text-muted: #6b7280 !important;
        }
      }
    `}</style>
      <DashboardShell
        navSections={navSections}
        activeTab={activeTab}
        onNavigate={tab => {
          setNewAuditRequested(false)
          setActiveTab(tab)
        }}
        navOpen={navOpen}
        onNavOpenChange={setNavOpen}
        mainRef={mainRef}
        topBar={
          <DashboardTopBar
            activeDomain={showNewAudit ? null : activeDomain}
            brand={showNewAudit ? undefined : brand}
            title={showNewAudit ? 'New audit' : undefined}
            statusLine={showNewAudit ? null : statusLine}
            onOpenNav={() => setNavOpen(true)}
            actions={
              <>
                {isRunning && (
                  <span
                    className='inline-flex items-center gap-1.5 px-2 py-1 font-mono text-xs'
                    style={{ color: 'var(--growth)' }}
                  >
                    <span
                      aria-hidden='true'
                      className='h-1.5 w-1.5 animate-pulse'
                      style={{ background: 'var(--growth)' }}
                    />
                    {doneTools.length}/{selected.size}
                  </span>
                )}
                {/* Report actions act on the CURRENT report — hidden on the
                    new-audit screen, where that report is not what is on
                    display. */}
                {!showNewAudit && activeDomain && hasResults && !isRunning && (
                  <>
                    <Button
                      variant='secondary'
                      size='sm'
                      leadingIcon={Plus}
                      loading={isSaving}
                      loadingText='Saving…'
                      onClick={saveRun}
                      className='hidden sm:inline-flex'
                    >
                      Save
                    </Button>
                    <IconButton
                      icon={Printer}
                      aria-label='Export as PDF'
                      tooltip='Export as PDF'
                      variant='ghost'
                      size='sm'
                      onClick={exportPDF}
                      disabled={!gscReady}
                    />
                    <IconButton
                      icon={Download}
                      aria-label='Export as JSON'
                      tooltip='Export as JSON'
                      variant='ghost'
                      size='sm'
                      onClick={exportJSON}
                      disabled={!gscReady}
                      className='hidden sm:inline-flex'
                    />
                    <IconButton
                      icon={ExternalLink}
                      aria-label='Copy a shareable report link'
                      tooltip={
                        shareFlash.success ? 'Link copied' : 'Share report'
                      }
                      variant='ghost'
                      size='sm'
                      onClick={shareReport}
                      className='sm:hidden'
                    />
                    <Button
                      variant='secondary'
                      size='sm'
                      leadingIcon={ExternalLink}
                      success={shareFlash.success}
                      successText='Link copied!'
                      onClick={shareReport}
                      className='hidden sm:inline-flex'
                    >
                      Share
                    </Button>
                  </>
                )}
                <AnimatedThemeToggle showScrollTooltip={false} />
                {showNewAudit && hasResults && !isRunning ? (
                  <Button
                    variant='secondary'
                    size='sm'
                    leadingIcon={ArrowLeft}
                    onClick={closeNewAudit}
                  >
                    <span className='hidden sm:inline'>Back to dashboard</span>
                    <span className='sm:hidden'>Back</span>
                  </Button>
                ) : (
                  !showNewAudit &&
                  !isRunning && (
                    <Button
                      variant='cta'
                      size='sm'
                      leadingIcon={Plus}
                      onClick={openNewAudit}
                    >
                      <span className='hidden sm:inline'>Run new audit</span>
                      <span className='sm:hidden'>New</span>
                    </Button>
                  )
                )}
              </>
            }
          />
        }
        progress={
          isRunning ? (
            <div
              className='h-1 w-full flex-shrink-0 bg-surface-inset'
              role='progressbar'
              aria-label='Audit progress'
              aria-valuemin={0}
              aria-valuemax={selected.size}
              aria-valuenow={doneTools.length}
            >
              <div
                className='h-full transition-all duration-700 ease-out'
                style={{
                  width: `${selected.size > 0 ? (doneTools.length / selected.size) * 100 : 5}%`,
                  background: 'linear-gradient(90deg, #008dcc, #F90B8A)',
                }}
              />
            </div>
          ) : null
        }
      >
        {/* ============================================================ */}
        {/* NEW AUDIT VIEW                                              */}
        {/* ============================================================ */}
        {/* Replaces the old inline form + separate onboarding block.
                  Same production handlers, same state — only the presentation
                  and the entry/exit transitions changed. Rendered instead of
                  the dashboard, never alongside it, so there is one audit flow
                  and not two. */}
        {openingRun && (
          <Panel className='flex items-center gap-3 px-6 py-8 sm:px-8'>
            <Loader2
              size={16}
              aria-hidden='true'
              className='shrink-0 animate-spin text-primary'
            />
            <span className='text-sm text-muted-foreground'>
              Opening your report&hellip;
            </span>
          </Panel>
        )}

        {openRunError && !openingRun && (
          <Panel
            as='div'
            role='alert'
            className='flex flex-wrap items-center justify-between gap-3 px-6 py-5 sm:px-8'
          >
            <span className='text-sm text-muted-foreground'>
              {openRunError}
            </span>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setOpenRunError(null)}
            >
              Dismiss
            </Button>
          </Panel>
        )}

        {showNewAudit && (
          <NewAuditView
            domain={domain}
            onDomainChange={(value: string) => {
              setDomain(value)
              setInputError(null)
              // Clear stale brand state on URL change. Without this the
              // brand from a restored session survives into a new audit
              // and renders in the header. Detection re-fires on blur.
              setBrandDetected(false)
              setBrand('')
              setBrandKeywords('')
              setBrandDescription('')
            }}
            onDomainBlur={(value?: string) => detectBrand(value ?? domain)}
            brand={brand}
            onBrandChange={value => handleBrandChange(value, domain)}
            entity={entity}
            entityConfirmPending={entityConfirmPending}
            onConfirmEntity={confirmEntity}
            category={category}
            onCategoryChange={setCategory}
            locale={locale}
            onLocaleChange={setLocale}
            brandDetected={brandDetected}
            detectingBrand={detectingBrand}
            brandDescription={brandDescription}
            inputError={inputError}
            selected={selected}
            onToggleTool={toggle}
            advancedOpen={advancedOpen}
            onAdvancedOpenChange={setAdvancedOpen}
            customizeOpen={customizeOpen}
            onCustomizeOpenChange={setCustomizeOpen}
            isRunning={isRunning}
            doneCount={doneTools.length}
            scanUsed={scanUsed}
            userTier={userTier}
            onUpgradeClick={() => setShowUpgradeModal(true)}
            onSubmit={handleRun}
            onBack={hasResults && !isRunning ? closeNewAudit : undefined}
            onReset={hasResults && !isRunning ? handleNewTest : undefined}
          />
        )}

        {/* ============================================================ */}
        {/* CONTENT AREA - each tab owns full space */}
        {/* ============================================================ */}

        {/* TAB: Overview (dashboard + thread log + issues) */}
        {/* `!showNewAudit` is load-bearing: without it the dashboard
                  stays mounted underneath the new-audit form, so clicking
                  "Run new audit" appeared to do nothing but scroll. The two
                  views are alternatives, never stacked. */}
        {!showNewAudit &&
          activeTab === 'overview' &&
          (hasResults || isRunning) && (
            <div className='space-y-4'>
              {/* Failure banner — surfaces tool errors above the dashboard
                      so users see them even when the global UI says "Audit
                      complete" (eval P2 #10). */}
              {(() => {
                const failedTools = Object.entries(tools).filter(
                  ([, s]) => s.status === 'error'
                )
                if (failedTools.length === 0) return null
                return (
                  <div
                    role='alert'
                    className='flex flex-wrap items-center justify-between gap-3 border border-destructive/40 bg-surface-card p-4'
                  >
                    <div className='flex items-center gap-2'>
                      <AlertTriangle
                        size={16}
                        className='text-[var(--destructive)] shrink-0'
                      />
                      <span className='text-sm text-foreground'>
                        {failedTools.length} of {selected.size} tools failed to
                        complete
                      </span>
                    </div>
                    <Button
                      variant='destructive'
                      size='sm'
                      rounded='md'
                      leadingIcon={RefreshCw}
                      onClick={() =>
                        failedTools.forEach(([key]) =>
                          retryTool(key as ToolKey)
                        )
                      }
                    >
                      Retry failed
                    </Button>
                  </div>
                )
              })()}
              {/* Early-running skeleton — avoid rendering the real
                      dashboard with score=0/F red while the first few tools
                      are still in flight (eval P3 #20). Once at least 3
                      tools have data, the unified score has enough signal
                      to render meaningfully. */}
              {isRunning && doneTools.length < 3 ? (
                <DashboardSkeleton
                  domain={activeDomain}
                  doneCount={doneTools.length}
                  totalCount={selected.size}
                />
              ) : (
                <DashboardOverview
                  tools={tools}
                  isRunning={isRunning}
                  unifiedScore={unifiedScore ?? 0}
                  unifiedGrade={unifiedGrade}
                  hasUnifiedScore={hasUnifiedScore}
                  scoredCount={unifiedScoreAgg.scoredCount}
                  withheldTools={unifiedScoreAgg.withheldTools}
                  strategistSlot={
                    hasResults && !isRunning ? (
                      <ReadinessStrategistEntry
                        grade={unifiedGrade}
                        score={unifiedScore ?? 0}
                        // Without this a withheld-only run rendered
                        // "You scored F (0/100)". The component already
                        // carries an ungradeable headline; `score` is ignored
                        // when hasScore is false.
                        hasScore={hasUnifiedScore}
                        suggestedQuestions={advisorSuggestedQuestions}
                        userTier={userTier}
                        onAsk={q =>
                          setStrategistTrigger(prev => ({
                            question: q,
                            nonce: (prev?.nonce ?? 0) + 1,
                          }))
                        }
                      />
                    ) : null
                  }
                  doneCount={doneTools.length}
                  totalCount={selected.size}
                  insights={insights}
                  activeDomain={activeDomain}
                  onToolClick={key => {
                    setExpandedTool(key)
                    setActiveTab('details')
                    setTimeout(
                      () =>
                        document
                          .getElementById('tool-detail-panel')
                          ?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          }),
                      100
                    )
                  }}
                  onRetry={retryTool}
                  previousScore={previousRun?.unifiedScore ?? null}
                  previousIssueCount={failingCheckCount(previousRun)}
                  previousScanDate={previousRun?.timestamp ?? null}
                  previousToolScores={previousRun?.toolScores ?? null}
                  actions={actions}
                  savedRunsCount={savedRuns.length}
                  onNavigateToActions={() => {
                    setActiveTab('actions')
                  }}
                  showActionPlan={actionPlanVisible}
                  onDismissActionPlan={() => setActionPlanVisible(false)}
                  userTier={userTier}
                  onUpgradeClick={() => setShowUpgradeModal(true)}
                  totalElapsed={
                    doneTools.length > 0
                      ? (() => {
                          const starts = doneTools
                            .filter(([, s]) => s.startedAt)
                            .map(([, s]) => s.startedAt!)
                          const ends = doneTools
                            .filter(([, s]) => s.finishedAt)
                            .map(([, s]) => s.finishedAt!)
                          if (starts.length === 0 || ends.length === 0)
                            return null
                          return (
                            (Math.max(...ends) - Math.min(...starts)) / 1000
                          )
                        })()
                      : null
                  }
                />
              )}

              {/* Thread log */}
              {logs.length > 0 && (
                <div className='overflow-hidden border border-hairline bg-surface-card'>
                  <button
                    onClick={() => setShowLogs(!showLogs)}
                    className='flex w-full items-center justify-between px-4 py-2.5 text-xs transition-colors hover:bg-surface-raised'
                  >
                    <span className='flex items-center gap-2 font-medium'>
                      <CircleDot
                        size={12}
                        className={isRunning ? 'animate-pulse' : ''}
                        style={{
                          color: isRunning
                            ? 'var(--growth)'
                            : 'var(--muted-foreground)',
                        }}
                      />
                      Thread Log
                      <span className='text-muted-foreground'>
                        ({logs.length})
                      </span>
                    </span>
                    {showLogs ? (
                      <ChevronUp size={12} className='text-muted-foreground' />
                    ) : (
                      <ChevronDown
                        size={12}
                        className='text-muted-foreground'
                      />
                    )}
                  </button>
                  {showLogs && (
                    <div
                      ref={logRef}
                      className='max-h-40 space-y-0.5 overflow-y-auto border-t border-hairline px-4 py-2 font-mono'
                      style={{ fontSize: '10px' }}
                    >
                      {logs.map((log, i) => {
                        const td = TOOLS.find(t => t.key === log.tool)
                        return (
                          <div key={i} className='flex items-start gap-3'>
                            <span
                              style={{
                                color: 'var(--muted-foreground)',
                                width: 52,
                                flexShrink: 0,
                              }}
                            >
                              {new Date(log.time).toLocaleTimeString('en-US', {
                                hour12: false,
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </span>
                            <span
                              className='font-semibold truncate'
                              style={{
                                color:
                                  log.tool === 'system'
                                    ? 'var(--muted-foreground)'
                                    : td?.color,
                                width: 70,
                                flexShrink: 0,
                              }}
                            >
                              {log.tool === 'system'
                                ? 'system'
                                : td?.name || log.tool}
                            </span>
                            <span
                              style={{
                                color:
                                  log.type === 'error'
                                    ? 'var(--cta)'
                                    : log.type === 'success'
                                      ? 'var(--growth)'
                                      : 'var(--muted-foreground)',
                              }}
                            >
                              {log.message}
                            </span>
                          </div>
                        )
                      })}
                      {isRunning && (
                        <div className='flex items-center gap-2 text-muted-foreground'>
                          <Loader2 size={12} className='animate-spin' />{' '}
                          Processing...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Smart CTAs - contextual upsell based on scores */}
              {!isRunning && hasResults && (
                <SmartCTA
                  scores={toolScoresMap}
                  domain={activeDomain}
                  unifiedScore={unifiedScore ?? 0}
                />
              )}

              {/* Cross-tool insights + ranked issues now render as the
                      final section INSIDE DashboardOverview (InsightsTabs),
                      so they sit with the rest of the audit rather than
                      after the thread log. */}
            </div>
          )}

        {/* TAB: Actions */}
        {activeTab === 'actions' && hasResults && !isRunning && (
          <ActionsPanel
            actions={actions}
            tools={tools}
            domain={domain}
            brand={brand}
            entityAssertable={isAssertableEntity(entity)}
          />
        )}

        {/* TAB: Details */}
        {activeTab === 'details' && hasResults && !isRunning && (
          <DetailsTab
            tools={tools}
            expandedTool={expandedTool}
            setExpandedTool={setExpandedTool}
          />
        )}

        {/* TAB: Domains (multi-domain agency view) */}
        {activeTab === 'domains' && (
          <MultiDomainDashboard
            savedRuns={savedRuns}
            onSelectDomain={run => {
              loadRun(run)
              setActiveTab('overview')
            }}
          />
        )}

        {/* TAB: History - always accessible */}
        {activeTab === 'history' && (
          <div className='flex flex-col gap-3'>
            {/* Removed 2026-07-26 and why:
                      · "Run History" heading + Clock icon — the sidebar
                        already names this destination.
                      · "Save Current Run" — audits auto-save on completion,
                        and the top bar still carries a manual Save.
                      · "Scheduled Re-scan Alerts / Coming Soon" — advertised
                        a feature that does not exist. Watch-domain in the
                        dashboard is the shipped version of it.
                      · The Score Trend chart — the hero sparkline is now the
                        one place a trend is read, so this was the same series
                        rendered twice. */}
            {savedRuns.length === 0 ? (
              <Panel className='px-6 py-10 text-center'>
                <span className='block text-sm text-muted-foreground'>
                  No saved runs yet.
                </span>
                <span className='mt-1 block text-[13px] text-instrument'>
                  Completed audits are saved here automatically.
                </span>
              </Panel>
            ) : (
              <div className='space-y-2'>
                {savedRuns.map(run => (
                  <Panel
                    key={run.id}
                    as='div'
                    className={`transition-colors ${
                      compareRunId === run.id
                        ? '!border-primary/50'
                        : 'hover:!border-hairline-strong'
                    }`}
                  >
                    {/* Row header — public/Radar History Run Row.html */}
                    <div className='flex flex-wrap items-center gap-x-4 gap-y-3 px-5 py-4 sm:px-7'>
                      <div className='flex items-baseline gap-2'>
                        <span
                          className='font-display text-[31px] font-bold leading-none tracking-[-0.02em] tabular-nums'
                          style={{
                            color: GRADE_COLORS[run.unifiedGrade],
                          }}
                        >
                          {run.unifiedScore}
                        </span>
                        <GradeChip grade={run.unifiedGrade} />
                      </div>

                      <span
                        aria-hidden='true'
                        className='hidden h-6 w-px bg-hairline sm:block'
                      />

                      <span className='text-[15px] font-semibold'>
                        {run.domain}
                      </span>

                      {editingLabel?.runId === run.id ? (
                        <form
                          onClick={e => e.stopPropagation()}
                          onSubmit={e => {
                            e.preventDefault()
                            commitClientLabel(run.id, editingLabel.value)
                          }}
                          className='inline-flex items-center gap-1'
                        >
                          <input
                            type='text'
                            value={editingLabel.value}
                            onChange={e =>
                              setEditingLabel({
                                runId: run.id,
                                value: e.target.value,
                              })
                            }
                            onKeyDown={e => {
                              if (e.key === 'Escape') {
                                e.preventDefault()
                                setEditingLabel(null)
                              }
                            }}
                            autoFocus
                            aria-label='Client label'
                            placeholder='Client label'
                            className='h-7 w-28 border border-[var(--control-border)] bg-surface-inset px-2 text-[11px] text-foreground caret-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
                          />
                          <IconButton
                            type='submit'
                            icon={Check}
                            aria-label='Save label'
                            variant='ghost'
                            size='xs'
                          />
                          <IconButton
                            icon={X}
                            aria-label='Cancel'
                            variant='ghost'
                            size='xs'
                            onClick={() => setEditingLabel(null)}
                          />
                        </form>
                      ) : run.clientLabel ? (
                        <span className='inline-flex items-center gap-1'>
                          <Chip color='var(--purple)'>{run.clientLabel}</Chip>
                          <IconButton
                            icon={Pencil}
                            aria-label={`Edit label "${run.clientLabel}"`}
                            variant='ghost'
                            size='xs'
                            onClick={e => {
                              e.stopPropagation()
                              setEditingLabel({
                                runId: run.id,
                                value: run.clientLabel ?? '',
                              })
                            }}
                          />
                          <IconButton
                            icon={X}
                            aria-label={`Remove label "${run.clientLabel}"`}
                            variant='ghost'
                            size='xs'
                            onClick={e => {
                              e.stopPropagation()
                              commitClientLabel(run.id, '')
                            }}
                          />
                        </span>
                      ) : (
                        /* Bordered chip, matching the reference — it
                                 reads as an affordance rather than stray text. */
                        <button
                          type='button'
                          onClick={e => {
                            e.stopPropagation()
                            setEditingLabel({ runId: run.id, value: '' })
                          }}
                          title='Add client label'
                          className='border border-hairline px-2 py-0.5 font-mono text-[11px] text-instrument transition-colors hover:border-hairline-strong hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
                        >
                          + label
                        </button>
                      )}

                      <span className='flex-1' />

                      <span className='font-mono text-xs text-muted-foreground'>
                        {new Date(run.timestamp).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <div className='flex items-center gap-2'>
                        {/* Blue at rest, filled blue when active. The
                                  colour is not decoration: this control ALREADY
                                  turned blue once a row was being compared, so
                                  tinting the resting state just makes the button
                                  and its active state read as one control.
                                  Load stays neutral — it has no equivalent state
                                  to encode, and an arbitrary hue would imply a
                                  meaning that is not there. */}
                        <Button
                          variant='secondary'
                          size='sm'
                          leadingIcon={GitCompare}
                          aria-pressed={compareRunId === run.id}
                          onClick={() =>
                            setCompareChoice(
                              compareRunId === run.id ? null : run.id
                            )
                          }
                          className={
                            compareRunId === run.id
                              ? 'border-primary/60 bg-primary/10 text-primary'
                              : 'border-primary/35 text-primary hover:border-primary/60 hover:bg-primary/10'
                          }
                        >
                          {compareRunId === run.id ? 'Comparing' : 'Compare'}
                        </Button>
                        {/* Filled against the outlined Compare, but
                                  NEUTRAL — the reference's `tone="solid"` is a
                                  surface fill with a strong border, not a brand
                                  CTA. A blue button repeated down 50 rows would
                                  out-shout the scores, which are the content. */}
                        <Button
                          variant='secondary'
                          size='sm'
                          leadingIcon={Play}
                          onClick={() => loadRun(run)}
                          className='border-hairline-strong bg-surface-raised hover:bg-surface-card'
                        >
                          Load
                        </Button>
                        <IconButton
                          icon={Trash2}
                          aria-label={`Delete run for ${run.domain}`}
                          variant='destructive'
                          size='sm'
                          onClick={() => deleteRun(run.id)}
                        />
                      </div>
                    </div>

                    {/* Per-tool chips: short label + score in its grade
                        colour, so a row scans without opening it. */}
                    <div className='flex flex-wrap gap-1.5 px-5 pb-4 sm:px-7'>
                      {TOOLS.map(t => {
                        const ts = run.toolScores[t.key]
                        if (!ts) return null
                        return (
                          <span
                            key={t.key}
                            className='inline-flex items-baseline gap-2 border border-hairline px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-hairline-strong hover:bg-surface-raised'
                          >
                            {t.shortName}
                            <span
                              className='font-mono font-semibold tabular-nums'
                              style={{
                                color:
                                  GRADE_COLORS[ts.grade] ??
                                  'var(--pm-text-muted)',
                              }}
                            >
                              {ts.score}
                            </span>
                          </span>
                        )
                      })}
                    </div>

                    {/* ── Run comparison ──────────────────────────
                              Redesigned 2026-07-26 against
                              public/Radar Run Comparison.html. */}
                    {compareRunId === run.id && hasResults && (
                      <div className='flex flex-col gap-4 border-t border-hairline px-5 pb-5 pt-5 sm:px-7'>
                        <div className='flex flex-wrap items-baseline gap-x-3.5 gap-y-1'>
                          <span className='font-display text-xl font-semibold tracking-[-0.02em]'>
                            This run vs current
                          </span>
                          <span className='font-mono text-[11px] text-instrument'>
                            {new Date(run.timestamp).toLocaleDateString(
                              'en-US',
                              { month: 'short', day: 'numeric' }
                            )}{' '}
                            then &middot; today now
                          </span>
                        </div>

                        {/* Two balanced columns of then -> now -> diff.
                                  Split down the middle so a 13-tool audit
                                  reads without a long single scroll. */}
                        <div className='grid grid-cols-1 gap-x-10 lg:grid-cols-2'>
                          {[TOOLS.slice(0, 7), TOOLS.slice(7)].map(
                            (group, gi) => (
                              <div
                                key={gi}
                                role='table'
                                aria-label={`Tool comparison, part ${gi + 1}`}
                                className='flex flex-col'
                              >
                                <div
                                  role='row'
                                  className='grid grid-cols-[1fr_56px_20px_56px_64px] gap-2 border-b border-hairline-strong py-2'
                                >
                                  <span className='instrument-label text-instrument'>
                                    Tool
                                  </span>
                                  <span className='instrument-label text-right text-instrument'>
                                    Then
                                  </span>
                                  <span />
                                  <span className='instrument-label text-right text-instrument'>
                                    Now
                                  </span>
                                  <span className='instrument-label text-right text-instrument'>
                                    Diff
                                  </span>
                                </div>
                                {group.map(t => {
                                  const currentValue =
                                    tools[t.key].data?.overallScore
                                  const current =
                                    typeof currentValue === 'number'
                                      ? currentValue
                                      : undefined
                                  const prev = run.toolScores[t.key]?.score
                                  if (
                                    current === undefined &&
                                    prev === undefined
                                  )
                                    return null
                                  const diff =
                                    current !== undefined && prev !== undefined
                                      ? current - prev
                                      : null
                                  return (
                                    <div
                                      key={t.key}
                                      role='row'
                                      className='grid grid-cols-[1fr_56px_20px_56px_64px] items-baseline gap-2 border-b border-hairline py-2 text-[13px] last:border-b-0'
                                    >
                                      <span className='truncate'>{t.name}</span>
                                      <span className='text-right font-mono tabular-nums text-instrument'>
                                        {prev ?? '--'}
                                      </span>
                                      <span
                                        aria-hidden='true'
                                        className='text-center text-instrument'
                                      >
                                        &rarr;
                                      </span>
                                      <span className='text-right font-mono font-semibold tabular-nums'>
                                        {current ?? '--'}
                                      </span>
                                      <span
                                        className='text-right font-mono tabular-nums'
                                        style={{
                                          color:
                                            diff === null || diff === 0
                                              ? 'var(--pm-text-muted)'
                                              : diff > 0
                                                ? 'var(--growth)'
                                                : 'var(--cta)',
                                          fontWeight:
                                            diff && diff !== 0 ? 600 : 400,
                                        }}
                                        aria-label={
                                          diff === null
                                            ? 'not comparable'
                                            : diff === 0
                                              ? 'no change'
                                              : diff > 0
                                                ? `${diff} points higher`
                                                : `${Math.abs(diff)} points lower`
                                        }
                                      >
                                        {diff === null
                                          ? '--'
                                          : diff === 0
                                            ? '='
                                            : diff > 0
                                              ? `+${diff}`
                                              : diff}
                                      </span>
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          )}
                        </div>

                        {/* Unified score, then -> now */}
                        <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-2 border border-hairline bg-surface-inset px-5 py-4'>
                          <InstrumentLabel tone='muted'>
                            Unified score
                          </InstrumentLabel>
                          <span className='font-display text-[25px] font-semibold tabular-nums text-muted-foreground'>
                            {run.unifiedScore}
                          </span>
                          <ArrowRight
                            size={16}
                            aria-hidden='true'
                            className='text-instrument'
                          />
                          <span className='font-display text-[25px] font-semibold tabular-nums'>
                            {unifiedScore ?? '--'}
                          </span>
                          {(() => {
                            if (unifiedScore === null) return null
                            const d = unifiedScore - run.unifiedScore
                            const color =
                              d > 0
                                ? 'var(--growth)'
                                : d < 0
                                  ? 'var(--cta)'
                                  : 'var(--pm-text-muted)'
                            const Icon =
                              d > 0 ? ArrowUp : d < 0 ? ArrowDown : Minus
                            return (
                              <span
                                className='inline-flex items-center gap-1 font-mono text-sm font-semibold tabular-nums'
                                style={{ color }}
                                aria-label={
                                  d > 0
                                    ? `${d} points higher than this run`
                                    : d < 0
                                      ? `${Math.abs(d)} points lower than this run`
                                      : 'no change'
                                }
                              >
                                <Icon size={14} aria-hidden='true' />
                                <span aria-hidden='true'>
                                  {d > 0 ? `+${d}` : d === 0 ? '=' : d}
                                </span>
                              </span>
                            )
                          })()}
                        </div>

                        {/* LLM answer diff. The blurred fake preview and
                                  its "Upgrade to Pro Retainer" overlay are gone
                                  — that gate was never tier-aware, so it sold a
                                  view nobody could reach. What remains is the
                                  measured summary. */}
                        <LLMAnswerDiff
                          currentRun={{
                            toolData: Object.fromEntries(
                              Object.entries(tools).map(([k, v]) => [k, v.data])
                            ),
                            timestamp: Date.now(),
                            unifiedScore: unifiedScore ?? 0,
                            domain: activeDomain || domain,
                          }}
                          previousRun={run}
                          onMarkAsWin={handleMarkAsWin}
                        />
                      </div>
                    )}
                  </Panel>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: Competitor */}
        {activeTab === 'competitor' && (
          <div className='space-y-5'>
            <div className='flex items-center gap-2'>
              <Swords size={16} style={{ color: 'var(--cta)' }} />
              <h2 className='text-sm font-semibold text-foreground'>
                Competitor Comparison
              </h2>
            </div>

            {!hasResults ? (
              <div className='border border-hairline bg-surface-card p-8 text-center'>
                <p className='text-xs text-muted-foreground'>
                  Run your own audit first, then compare against a competitor.
                </p>
              </div>
            ) : (
              <>
                {/* Competitor input */}
                <form
                  onSubmit={runCompetitor}
                  className='border border-hairline bg-surface-card p-4'
                >
                  <p className='text-xs mb-3'>
                    Enter a competitor domain to run the same tools and compare
                    results side-by-side.
                  </p>
                  <div className='flex gap-3 mb-3'>
                    <Input
                      srLabel='Competitor domain'
                      type='text'
                      inputSize='md'
                      rounded='md'
                      value={competitorDomain}
                      onChange={e => setCompetitorDomain(e.target.value)}
                      placeholder='Competitor domain (e.g. competitor.com)'
                      disabled={competitorRunning}
                      containerClassName='flex-1'
                    />
                    <Button
                      type='submit'
                      variant='cta'
                      size='md'
                      rounded='md'
                      loading={competitorRunning}
                      loadingText='Running...'
                      leadingIcon={Swords}
                      disabled={!competitorDomain.trim()}
                    >
                      Run Comparison
                    </Button>
                  </div>
                  <div className='flex gap-3'>
                    <Input
                      srLabel='Competitor brand'
                      type='text'
                      inputSize='sm'
                      rounded='md'
                      value={competitorBrand}
                      onChange={e => setCompetitorBrand(e.target.value)}
                      placeholder='Brand name (auto-detected)'
                      disabled={competitorRunning}
                      containerClassName='flex-1'
                    />
                    <Input
                      srLabel='Competitor category'
                      type='text'
                      inputSize='sm'
                      rounded='md'
                      value={competitorCategory}
                      onChange={e => setCompetitorCategory(e.target.value)}
                      placeholder='Category (auto-detected)'
                      disabled={competitorRunning}
                      containerClassName='flex-1'
                    />
                  </div>
                </form>

                {/* Comparison table */}
                {(compDoneTools.length > 0 || competitorRunning) && (
                  <div className='overflow-hidden border border-hairline bg-surface-card'>
                    {/* Header */}
                    <div className='grid grid-cols-4 border-b border-hairline'>
                      <div className='p-3 text-[11px] font-bold text-muted-foreground'>
                        Tool
                      </div>
                      <div className='p-3 text-center'>
                        <p
                          className='text-[11px] font-bold'
                          style={{ color: 'var(--primary)' }}
                        >
                          {activeDomain}
                        </p>
                      </div>
                      <div className='p-3 text-center'>
                        <p
                          className='text-[11px] font-bold'
                          style={{ color: 'var(--cta)' }}
                        >
                          {competitorDomain.trim()
                            ? new URL(
                                competitorDomain.startsWith('http')
                                  ? competitorDomain
                                  : `https://${competitorDomain}`
                              ).hostname
                            : 'Competitor'}
                        </p>
                      </div>
                      <div className='p-3 text-center text-[11px] font-bold text-muted-foreground'>
                        Diff
                      </div>
                    </div>

                    {/* Unified score row */}
                    <div className='grid grid-cols-4 border-b border-hairline bg-surface-inset'>
                      <div className='p-3 text-xs font-semibold text-foreground'>
                        Unified Score
                      </div>
                      <div className='p-3 text-center'>
                        <span
                          className='text-lg font-black'
                          style={{
                            color: GRADE_COLORS[unifiedGrade],
                          }}
                        >
                          {unifiedScore ?? '--'}
                        </span>
                        <span
                          className='text-xs font-bold ml-1'
                          style={{
                            color: GRADE_COLORS[unifiedGrade],
                          }}
                        >
                          {unifiedGrade}
                        </span>
                      </div>
                      <div className='p-3 text-center'>
                        {competitorRunning ? (
                          <Loader2
                            size={14}
                            className='animate-spin mx-auto text-muted-foreground'
                          />
                        ) : compScores.length > 0 ? (
                          <>
                            <span
                              className='text-lg font-black'
                              style={{
                                color: GRADE_COLORS[compUnifiedGrade],
                              }}
                            >
                              {compUnifiedScore}
                            </span>
                            <span
                              className='text-xs font-bold ml-1'
                              style={{
                                color: GRADE_COLORS[compUnifiedGrade],
                              }}
                            >
                              {compUnifiedGrade}
                            </span>
                          </>
                        ) : (
                          <span className='text-muted-foreground'>-</span>
                        )}
                      </div>
                      <div className='p-3 text-center'>
                        {compScores.length > 0 &&
                          (() => {
                            const d = (unifiedScore ?? 0) - compUnifiedScore
                            return (
                              <span
                                className='inline-flex items-center gap-0.5 text-sm font-bold tabular-nums'
                                style={{
                                  color:
                                    d > 0
                                      ? 'var(--growth)'
                                      : d < 0
                                        ? 'var(--destructive)'
                                        : 'var(--muted-foreground)',
                                }}
                                aria-label={
                                  d > 0
                                    ? `${d} points higher`
                                    : d < 0
                                      ? `${Math.abs(d)} points lower`
                                      : 'no change'
                                }
                              >
                                {d > 0 ? (
                                  <ArrowUp size={12} aria-hidden='true' />
                                ) : d < 0 ? (
                                  <ArrowDown size={12} aria-hidden='true' />
                                ) : (
                                  <Minus size={12} aria-hidden='true' />
                                )}
                                <span aria-hidden='true'>
                                  {d > 0 ? `+${d}` : d === 0 ? '=' : d}
                                </span>
                              </span>
                            )
                          })()}
                      </div>
                    </div>

                    {/* Per-tool rows */}
                    {TOOLS.map(t => {
                      const Icon = t.icon
                      const myScoreValue = tools[t.key].data?.overallScore
                      const myScore =
                        typeof myScoreValue === 'number'
                          ? myScoreValue
                          : undefined
                      const myGrade = tools[t.key].data?.grade as
                        | string
                        | undefined
                      const compState = competitorTools[t.key]
                      const compScoreValue = compState.data?.overallScore
                      const compScore =
                        typeof compScoreValue === 'number'
                          ? compScoreValue
                          : undefined
                      const compGrade = compState.data?.grade as
                        | string
                        | undefined
                      const diff =
                        myScore !== undefined && compScore !== undefined
                          ? myScore - compScore
                          : null

                      return (
                        <div
                          key={t.key}
                          className='grid grid-cols-4 border-b border-hairline last:border-0'
                        >
                          <div className='p-3 flex items-center gap-2'>
                            <Icon size={12} style={{ color: t.color }} />
                            <span className='text-xs'>{t.name}</span>
                          </div>
                          <div className='p-3 text-center'>
                            {myScore !== undefined ? (
                              <>
                                <span
                                  className='text-sm font-bold tabular-nums'
                                  style={{
                                    color: GRADE_COLORS[myGrade!],
                                  }}
                                >
                                  {myScore}
                                </span>
                                <span
                                  className='text-[11px] font-bold ml-1'
                                  style={{
                                    color: GRADE_COLORS[myGrade!],
                                  }}
                                >
                                  {myGrade}
                                </span>
                              </>
                            ) : (
                              <span className='text-xs text-muted-foreground'>
                                -
                              </span>
                            )}
                          </div>
                          <div className='p-3 text-center'>
                            {compState.status === 'running' ? (
                              <Loader2
                                size={12}
                                className='animate-spin mx-auto'
                                style={{ color: t.color }}
                              />
                            ) : compScore !== undefined ? (
                              <>
                                <span
                                  className='text-sm font-bold tabular-nums'
                                  style={{
                                    color: GRADE_COLORS[compGrade!],
                                  }}
                                >
                                  {compScore}
                                </span>
                                <span
                                  className='text-[11px] font-bold ml-1'
                                  style={{
                                    color: GRADE_COLORS[compGrade!],
                                  }}
                                >
                                  {compGrade}
                                </span>
                              </>
                            ) : compState.status === 'error' ? (
                              <span
                                className='text-[11px]'
                                style={{ color: 'var(--cta)' }}
                              >
                                Error
                              </span>
                            ) : (
                              <span className='text-xs text-muted-foreground'>
                                -
                              </span>
                            )}
                          </div>
                          <div className='p-3 text-center'>
                            {diff !== null && (
                              <span
                                className='inline-flex items-center gap-0.5 text-sm font-bold tabular-nums'
                                style={{
                                  color:
                                    diff > 0
                                      ? 'var(--growth)'
                                      : diff < 0
                                        ? 'var(--destructive)'
                                        : 'var(--muted-foreground)',
                                }}
                                aria-label={
                                  diff > 0
                                    ? `${diff} points higher`
                                    : diff < 0
                                      ? `${Math.abs(diff)} points lower`
                                      : 'no change'
                                }
                              >
                                {diff > 0 ? (
                                  <ArrowUp size={12} aria-hidden='true' />
                                ) : diff < 0 ? (
                                  <ArrowDown size={12} aria-hidden='true' />
                                ) : (
                                  <Minus size={12} aria-hidden='true' />
                                )}
                                <span aria-hidden='true'>
                                  {diff > 0
                                    ? `+${diff}`
                                    : diff === 0
                                      ? '='
                                      : diff}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* TAB: Threads */}
        {activeTab === 'threads' && hasResults && !isRunning && (
          <ThreadsPanel
            threads={threads}
            actions={actions}
            tools={tools}
            domain={domain}
            brand={brand}
            entityAssertable={isAssertableEntity(entity)}
            onRerun={retryTool}
            progressMap={threadProgress}
            onToggleComplete={handleToggleComplete}
          />
        )}

        {/* The onboarding block that used to sit here (dashboard
                  preview, how-it-works, quick-start domains, tool list) is now
                  part of NewAuditView above, which owns the pre-audit screen.
                  Keeping both would have shown the tool inventory twice. */}
      </DashboardShell>

      {/* Floating overlays live outside the shell so they are not clipped by
          the scroll container, but they still need the dashboard palette —
          hence the second data-surface scope. */}
      <div data-surface='radar-dashboard' className='text-foreground'>
        <RadarTour />
        {/* Advisor Chat (floating) */}
        <div data-advisor-chat>
          <AdvisorChat
            domain={activeDomain}
            auditContext={auditContext}
            toolResults={tools}
            insights={insights}
            actions={actions}
            suggestedQuestions={advisorSuggestedQuestions}
            userTier={userTier}
            onUpgradeClick={() => setShowUpgradeModal(true)}
            externalTrigger={strategistTrigger}
          />
        </div>
        {/* Upgrade Modal - shown when free scan is used up */}
        {showUpgradeModal && (
          <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4'
            onClick={() => setShowUpgradeModal(false)}
          >
            <div
              className='w-full max-w-md border border-hairline bg-surface-card p-8'
              onClick={e => e.stopPropagation()}
            >
              <div className='text-center mb-6'>
                <div className='mb-4 inline-flex h-12 w-12 items-center justify-center border border-hairline bg-surface-inset'>
                  <Sparkles size={20} style={{ color: 'var(--purple)' }} />
                </div>
                <h3 className='text-lg font-bold mb-1 text-foreground'>
                  Your free scan is complete
                </h3>
                <p className='text-sm text-muted-foreground'>
                  Pick a plan to keep auditing. Credits never expire.
                </p>
              </div>

              <div className='space-y-3'>
                <button
                  type='button'
                  onClick={() => {
                    setShowUpgradeModal(false)
                    setShowPackModal(true)
                  }}
                  className='block w-full bg-[var(--primary-fill)] px-5 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-[#006ba1]'
                >
                  Buy Audit Pack &mdash; from $5
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setShowUpgradeModal(false)
                    void openCheckout('retainer', 'upgrade_modal')
                  }}
                  className='block w-full border border-hairline-strong px-5 py-3 text-center text-sm font-semibold text-foreground transition-colors hover:bg-surface-raised'
                >
                  Start Pro Retainer &mdash; $199/mo
                </button>
              </div>

              <div className='mt-6 border border-hairline bg-surface-inset p-4'>
                <p className='text-xs font-semibold mb-3 text-muted-foreground'>
                  What you unlock:
                </p>
                <div className='space-y-2'>
                  {[
                    'Re-audit after every fix',
                    'PDF export + saved history',
                    'Competitor comparison',
                    'Unlimited Readiness Strategist per audit',
                    'Weekly pulse re-scan (Pro Retainer)',
                  ].map(item => (
                    <div key={item} className='flex items-center gap-2'>
                      <CheckCircle2
                        size={12}
                        style={{ color: 'var(--growth)' }}
                      />
                      <span className='text-xs'>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type='button'
                onClick={() => setShowUpgradeModal(false)}
                className='block w-full text-center text-xs text-muted-foreground hover:text-foreground mt-4 transition-colors'
              >
                Maybe later
              </button>
            </div>
          </div>
        )}
        {showPackModal && <PackModal onClose={() => setShowPackModal(false)} />}
        {/*
          Session Mismatch Modal — fires when /audit/charge returns 409.
          The user has credits or an active subscription but the consume
          call missed them. Recovery path is a fresh sign-in link, not a
          purchase. See migration 080 for the anomaly types this catches.
        */}
        {sessionMismatch && (
          <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4'
            onClick={() => {
              if (sendLinkStatus !== 'sending') {
                setSessionMismatch(null)
              }
            }}
          >
            <div
              className='w-full max-w-md border border-hairline bg-surface-card p-8'
              onClick={e => e.stopPropagation()}
            >
              <div className='text-center mb-6'>
                <div className='mb-4 inline-flex h-12 w-12 items-center justify-center border border-hairline bg-surface-inset'>
                  <RefreshCw size={20} style={{ color: 'var(--purple)' }} />
                </div>
                <h3 className='text-lg font-bold mb-1 text-foreground'>
                  Your credits are fine
                </h3>
                <p className='text-sm text-muted-foreground'>
                  Your session needs to refresh before we can reach them. This
                  usually happens after a long idle period or when a corporate
                  browser clears cookies.
                </p>
              </div>

              {sessionMismatch.email ? (
                <div className='mb-4 border border-hairline bg-surface-inset p-3 text-center'>
                  <p className='text-[11px] uppercase tracking-wider text-foreground/70 font-semibold mb-1'>
                    Signed in as
                  </p>
                  <p className='text-sm font-medium text-foreground'>
                    {sessionMismatch.email}
                  </p>
                </div>
              ) : (
                <div className='mb-4'>
                  <Input
                    label='Email on your paid account'
                    type='email'
                    inputSize='md'
                    rounded='md'
                    leadingIcon={Mail}
                    value={sendLinkManualEmail}
                    onChange={e => setSendLinkManualEmail(e.target.value)}
                    placeholder='you@company.com'
                    disabled={sendLinkStatus === 'sending'}
                    autoFocus
                    helpText='We will send a fresh sign-in link here.'
                  />
                </div>
              )}

              {sendLinkStatus === 'sent' ? (
                <div className='border border-growth/35 bg-surface-inset p-4 text-center'>
                  <Mail
                    size={20}
                    className='mx-auto mb-2'
                    style={{ color: 'var(--growth)' }}
                  />
                  <p className='text-sm font-semibold text-foreground mb-1'>
                    Check your inbox
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    A fresh sign-in link is on its way to{' '}
                    {sessionMismatch.email ||
                      sendLinkManualEmail ||
                      'your email'}
                    . Click it, and your audit will work right after.
                  </p>
                </div>
              ) : (
                <button
                  type='button'
                  disabled={
                    sendLinkStatus === 'sending' ||
                    !(sessionMismatch.email || sendLinkManualEmail.trim())
                  }
                  onClick={async () => {
                    const targetEmail =
                      sessionMismatch.email || sendLinkManualEmail.trim()
                    if (!targetEmail) return
                    setSendLinkStatus('sending')
                    setSendLinkError(null)
                    try {
                      const res = await fetch('/api/platform/auth/send-link', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: targetEmail }),
                      })
                      if (res.ok) {
                        setSendLinkStatus('sent')
                      } else {
                        const body = await res.json().catch(() => null)
                        setSendLinkError(
                          body?.message ||
                            'Could not send the link. Try again in a minute.'
                        )
                        setSendLinkStatus('error')
                      }
                    } catch (err) {
                      setSendLinkError(
                        err instanceof Error
                          ? err.message
                          : 'Network error. Try again.'
                      )
                      setSendLinkStatus('error')
                    }
                  }}
                  className='block w-full bg-[var(--primary-fill)] px-5 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-[#006ba1] disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {sendLinkStatus === 'sending' ? (
                    <span className='inline-flex items-center gap-2'>
                      <Loader2 size={14} className='animate-spin' />
                      Sending fresh link...
                    </span>
                  ) : (
                    <span className='inline-flex items-center gap-2 justify-center'>
                      <Mail size={14} />
                      Send me a fresh sign-in link
                    </span>
                  )}
                </button>
              )}

              {sendLinkStatus === 'error' && sendLinkError && (
                <p
                  className='mt-3 text-xs text-center'
                  style={{ color: '#ef4444' }}
                >
                  {sendLinkError}
                </p>
              )}

              <p className='mt-4 text-[11px] text-center text-muted-foreground'>
                Our observability layer flagged this automatically.{' '}
                {sessionMismatch.email
                  ? `If it keeps happening, reply to any Pixelmojo email and reference ${sessionMismatch.email} — we have the breadcrumb trail.`
                  : 'If it keeps happening, contact founders@pixelmojo.io.'}
              </p>

              <button
                type='button'
                onClick={() => setSessionMismatch(null)}
                disabled={sendLinkStatus === 'sending'}
                className='block w-full text-center text-xs text-muted-foreground hover:text-foreground mt-4 transition-colors disabled:opacity-60'
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// SidebarItem moved to components/radar/shell/DashboardSidebar.tsx, where the
// nav model drives both the desktop rail and the mobile drawer.
// ToolResultCard, ToolDetailPanel, DetailsTab and ActionsPanel are imported
// from @/components/radar/. ScoreRing was retired with the overhaul; the
// score now renders through ScoreHero + GradeScale.
